import {
  batch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  ParentProps,
  Show,
  untrack,
  type Accessor,
} from "solid-js"
import { makeEventListener } from "@solid-primitives/event-listener"
import { useLocation, useNavigate, useParams } from "@solidjs/router"
import { useLayout, LocalProject } from "@/context/layout"
import { useGlobalSync } from "@/context/global-sync"
import { Persist, persisted } from "@/utils/persist"
import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { decode64 } from "@/utils/base64"
import { ResizeHandle } from "@openanalyst-ai/ui/resize-handle"
import { Button } from "@openanalyst-ai/ui/button"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { Icon, type IconName } from "@openanalyst-ai/ui/icon"
import { DropdownMenu } from "@openanalyst-ai/ui/dropdown-menu"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { getFilename } from "@openanalyst-ai/core/util/path"
import { Session, type Config, type Message } from "@openanalyst-ai/sdk/v2/client"
import { usePlatform } from "@/context/platform"
import { useSettings } from "@/context/settings"
import { createStore, produce, reconcile } from "solid-js/store"
import { DragDropProvider, DragDropSensors, DragOverlay, SortableProvider, closestCenter } from "@thisbeyond/solid-dnd"
import type { DragEvent } from "@thisbeyond/solid-dnd"
import { useProviders } from "@/hooks/use-providers"
import { showToast, Toast, toaster } from "@openanalyst-ai/ui/toast"
import { useGlobalSDK } from "@/context/global-sdk"
import { LocalProvider } from "@/context/local"
import { SDKProvider } from "@/context/sdk"
import { SyncProvider } from "@/context/sync"
import { clearWorkspaceTerminals } from "@/context/terminal"
import { AppStartupWelcomeDialog } from "@/components/app-startup-welcome-dialog"
import { OnboardingWorkspaceIntroDialog } from "@/components/onboarding-workspace-intro-dialog"
import { OnboardingFirstRunDialog } from "@/components/onboarding-first-run-dialog"
import { dropSessionCaches, pickSessionCacheEvictions } from "@/context/global-sync/session-cache"
import {
  clearSessionPrefetchInflight,
  clearSessionPrefetch,
  getSessionPrefetch,
  isSessionPrefetchCurrent,
  runSessionPrefetch,
  setSessionPrefetch,
  shouldSkipSessionPrefetch,
} from "@/context/global-sync/session-prefetch"
import { useNotification } from "@/context/notification"
import { usePermission } from "@/context/permission"
import { Binary } from "@openanalyst-ai/core/util/binary"
import { retry } from "@openanalyst-ai/core/util/retry"
import { playSoundById } from "@/utils/sound"
import { createAim } from "@/utils/aim"
import { setNavigate } from "@/utils/notification-click"
import { Worktree as WorktreeState } from "@/utils/worktree"
import { setSessionHandoff } from "@/pages/session/handoff"

import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { useTheme, type ColorScheme } from "@openanalyst-ai/ui/theme/context"
import { useCommand, type CommandOption } from "@/context/command"
import { ConstrainDragXAxis, getDraggableId } from "@/utils/solid-dnd"
import { DebugBar } from "@/components/debug-bar"
import { Titlebar } from "@/components/titlebar"
import type { SettingsTab } from "@/components/dialog-settings"
import { useServer } from "@/context/server"
import { useAuth } from "@/context/auth"
import { useLanguage, type Locale } from "@/context/language"
import { pathKey } from "@/utils/path-key"
import { isFilesystemRootDirectory } from "@/utils/project-directory"
import {
  readSmartworkReturnPath,
  smartworkAppWorkspaceFromReturnPath,
  smartworkCloudReviewPath,
  smartworkProjectFromSearch,
  smartworkViewPath,
  writeSmartworkReturnPath,
} from "@/utils/smartwork"
import { resolveSmartworkWorkspaceResolverDetailed } from "@/services/smartwork-project-identity"
import {
  readSmartworkProjectServices,
  smartworkProjectServicesEnabledFor,
  subscribeSmartworkProjectServicesChanged,
} from "@/services/smartwork-project-services"
import { clearPluginRestartIntent, readPluginRestartIntent } from "@/services/plugins"
import { DialogCustomTemplate } from "@/components/dialog-custom-template"
import { DialogNewProject } from "@/components/dialog-new-project"
import { DialogProjectStart } from "@/components/project-start-chooser"
import { createHostedCustomProject } from "@/services/hosted-custom-project"
import { hostedDirectoryPickerAvailable, pickHostedFolderWithNativePicker } from "@/services/hosted-folder-picker"
import {
  isProjectStarterTemplateID,
  isUnknownStarterProjectTemplateError,
  starterProjectFirstRunPath,
  starterProjectRootLabel as formatStarterProjectRootLabel,
  starterProjectRootPath as resolveStarterProjectRootPath,
  type ProjectStarterTemplateID,
} from "@/services/project-starters"
import { displayPath } from "@/utils/display-path"
import {
  layoutHostedFolderPickerFailedMessage,
  layoutHostedProjectUploadFailedMessage,
  layoutOpenDirectoryFailedMessage,
  layoutProjectLabelSaveFailedMessage,
  layoutSessionRenameFailedMessage,
  layoutStarterProjectCreateFailedMessage,
  layoutStarterProjectUnsupportedTemplateMessage,
  layoutThreadLabelSaveFailedMessage,
  layoutWorkspaceCreateFailedMessage,
  layoutWorkspaceDeleteFailedMessage,
  layoutWorkspaceGitInitFailedMessage,
  layoutWorkspaceResetFailedMessage,
} from "@/pages/layout-copy"
import {
  displayName,
  effectiveWorkspaceOrder,
  hasProjectPermissions,
  latestRootSession,
  resolveAutoselectProject,
  sortedRootSessions,
  workspaceKey,
} from "./layout/helpers"
import { hasUnseenResponseNotification } from "./layout/sidebar-project-notification"
import { threadHasFeedbackRequest, threadIsActive } from "./layout/status"
import { toggleProjectWorkspaceSetting, upsertInitializedProject } from "./layout/workspace-git-onboarding"
import {
  defaultThreadViewState,
  isPinnedThread,
  layerKind,
  pinnedSortValue,
  projectManagementConfig,
  projectViewKey,
  resolveProjectView,
  resolveThreadView,
  threadViewKey,
  viewEnabled,
  type ThreadStage,
  type ViewLayerKind,
} from "./layout/view-config"
import { upsertActiveSession } from "./layout/archived-chats"
import {
  billingDeepLinkPath,
  collectBillingDeepLinks,
  collectNewSessionDeepLinks,
  collectOpenProjectDeepLinks,
  deepLinkEvent,
  drainPendingDeepLinks,
  missingLocalProjectDeepLinkPath,
  parseDeepLink,
  parseBillingDeepLink,
  parseNewSessionDeepLink,
  resolveProjectDeepLinkDirectory,
  sessionDeepLinkSearch,
  type MissingLocalProjectDeepLinkAction,
  type NewSessionDeepLink,
  type OpenProjectDeepLink,
} from "./layout/deep-links"
import { createInlineEditorController } from "./layout/inline-editor"
import {
  LocalWorkspace,
  SortableWorkspace,
  WorkspaceDragOverlay,
  type WorkspaceSidebarContext,
} from "./layout/sidebar-workspace"
import { ProjectDragOverlay, SortableProject, type ProjectSidebarContext } from "./layout/sidebar-project"
import { AppSidebarPanel } from "./layout/app-sidebar-panel"
import { appSidebarPluginThreadOrigin } from "./layout/app-sidebar-panel-state"
import { ProjectPluginRail } from "./layout/sidebar-plugin-rail"
import { SidebarContent } from "./layout/sidebar-shell"
import { SessionItem, type SessionItemContext } from "./layout/sidebar-items"
import { ThreadActionsProvider } from "./layout/thread-actions"
import { pluginAppSessionHref, type RuntimePluginApp } from "@/plugins/runtime"

let appStartupWelcomeShown = false

export default function Layout(props: ParentProps) {
  const [store, setStore, , ready] = persisted(
    Persist.global("layout.page", ["layout.page.v1"]),
    createStore({
      lastProjectSession: {} as { [directory: string]: { directory: string; id: string; at: number } },
      activeProject: undefined as string | undefined,
      activeWorkspace: undefined as string | undefined,
      workspaceOrder: {} as Record<string, string[]>,
      workspaceName: {} as Record<string, string>,
      workspaceBranchName: {} as Record<string, Record<string, string>>,
      workspaceExpanded: {} as Record<string, boolean>,
      projectChatsExpanded: {} as Record<string, boolean>,
      threadView: defaultThreadViewState(),
      gettingStartedDismissed: false,
      startupWelcomeVersion: "",
    }),
  )

  const pageReady = createMemo(() => ready())
  const [smartworkProjectServices, setSmartworkProjectServices] = createSignal(readSmartworkProjectServices())

  let scrollContainerRef: HTMLDivElement | undefined
  let dialogRun = 0
  let dialogDead = false
  let onboardingIntroShownFor = ""
  const workspaceTogglePending = new Set<string>()

  const params = useParams()
  const globalSDK = useGlobalSDK()
  const globalSync = useGlobalSync()
  const layout = useLayout()
  const layoutReady = createMemo(() => layout.ready())
  const auth = useAuth()
  const platform = usePlatform()
  const settings = useSettings()
  const server = useServer()
  const notification = useNotification()
  const permission = usePermission()
  const navigate = useNavigate()
  setNavigate(navigate)
  const providers = useProviders()
  const dialog = useDialog()
  const command = useCommand()
  const theme = useTheme()
  const language = useLanguage()

  function rejectFilesystemRootProject(directory: string) {
    if (!isFilesystemRootDirectory(directory)) return false
    showToast({
      title: language.t("project.start.createFailed.title"),
      description: "Choose a project folder instead of the filesystem root.",
      variant: "error",
    })
    return true
  }

  onMount(() => {
    const unsubscribe = subscribeSmartworkProjectServicesChanged(() =>
      setSmartworkProjectServices(readSmartworkProjectServices()),
    )
    onCleanup(unsubscribe)
  })

  const homedir = createMemo(() => globalSync.data.path.home)
  const starterProjectRoot = createMemo(() => settings.general.starterProjectRoot())
  const starterProjectRootPath = createMemo(() => resolveStarterProjectRootPath(starterProjectRoot(), homedir()))
  const starterProjectRootLabel = createMemo(() =>
    displayPath(formatStarterProjectRootLabel(starterProjectRoot(), homedir()), homedir()),
  )
  const initialDirectory = decode64(params.dir)
  const location = useLocation()
  const miniChatRoute = createMemo(() => /^\/[^/]+\/mini-chat(?:\/|$)/.test(location.pathname))

  /**
   * A popped-out plugin app. The window exists to be ONE app next to ONE chat, so it
   * carries no project rail and no session sidebar — there is nothing to navigate to.
   * The titlebar stays (that is where the handle lives).
   */
  const pluginPopoutRoute = createMemo(() => new URLSearchParams(location.search).get("popout") === "1")
  const currentRoutePath = () => `${location.pathname}${location.search}${location.hash}`
  createEffect(() => {
    const slug = params.dir
    if (!slug) return
    const query = new URLSearchParams(location.search)
    if (query.get("onboardingIntro") !== "1") return
    appStartupWelcomeShown = true
    const key = `${slug}:${query.get("starterTemplate") ?? ""}`
    if (onboardingIntroShownFor === key) return
    onboardingIntroShownFor = key
    const directory = decode64(slug)
    if (!directory) return
    const rawTemplate = query.get("starterTemplate")
    const templateId = isProjectStarterTemplateID(rawTemplate) ? rawTemplate : null
    query.delete("onboardingIntro")
    query.delete("starterTemplate")
    const search = query.toString()
    navigate(`${location.pathname}${search ? `?${search}` : ""}${location.hash}`, { replace: true })
    queueMicrotask(() => {
      dialog.show(() => (
        <OnboardingWorkspaceIntroDialog
          templateId={templateId}
          directory={directory}
          onContinue={() =>
            queueMicrotask(() =>
              dialog.show(
                () => <OnboardingFirstRunDialog templateId={templateId} directory={directory} />,
                undefined,
                { dismissible: false },
              ),
            )
          }
        />
      ))
    })
  })
  const startupWelcomeName = createMemo(() => {
    const fullName = auth.state().user?.fullName?.trim()
    if (fullName) return fullName
    const email = auth.state().user?.email?.trim() || auth.state().session?.email?.trim()
    return email?.split("@")[0] || "there"
  })
  const startupWelcomeVersion = createMemo(() => platform.version ?? "unknown")
  createEffect(() => {
    if (appStartupWelcomeShown) return
    if (!auth.state().authenticated) return
    if (!pageReady()) return
    if (new URLSearchParams(location.search).get("onboardingIntro") === "1") return
    if (store.startupWelcomeVersion === startupWelcomeVersion()) {
      appStartupWelcomeShown = true
      return
    }
    appStartupWelcomeShown = true
    const timer = window.setTimeout(() => {
      if (dialogDead) return
      setStore("startupWelcomeVersion", startupWelcomeVersion())
      dialog.show(() => <AppStartupWelcomeDialog name={startupWelcomeName()} />)
    }, 650)
    onCleanup(() => window.clearTimeout(timer))
  })
  const standaloneRoute = createMemo(() => location.pathname === "/smartwork")
  const route = createMemo(() => {
    const slug = params.dir
    if (!slug) return { slug, dir: "" }
    const dir = decode64(slug)
    if (!dir) return { slug, dir: "" }
    const store = globalSync.peek(dir, { bootstrap: false })
    return {
      slug,
      store,
      dir: store[0].path.directory || dir,
    }
  })
  const availableThemeEntries = createMemo(() => theme.ids().map((id) => [id, theme.themes()[id]] as const))
  const colorSchemeOrder: ColorScheme[] = ["system", "light", "dark"]
  const colorSchemeKey: Record<ColorScheme, "theme.scheme.system" | "theme.scheme.light" | "theme.scheme.dark"> = {
    system: "theme.scheme.system",
    light: "theme.scheme.light",
    dark: "theme.scheme.dark",
  }
  const colorSchemeLabel = (scheme: ColorScheme) => language.t(colorSchemeKey[scheme])
  const currentDir = createMemo(() => route().dir)
  createEffect(
    on(currentDir, (directory) => {
      if (!directory || platform.platform !== "desktop") return
      void platform.activateAppSidecars?.(directory)
    }),
  )
  const smartworkProjectFromLocation = createMemo(() =>
    standaloneRoute() ? smartworkProjectFromSearch(location.search) : null,
  )
  const smartworkReturnDirectory = createMemo(() =>
    standaloneRoute()
      ? (smartworkAppWorkspaceFromReturnPath(readSmartworkReturnPath())?.directory ??
        layout.projects.list().find((project) => project.worktree === server.projects.last())?.worktree ??
        "")
      : "",
  )
  const smartworkProjectDirectory = createMemo(() => smartworkProjectFromLocation()?.directory ?? "")
  const sidebarAppDirectory = createMemo(
    () => currentDir() || smartworkProjectDirectory() || smartworkReturnDirectory(),
  )
  const activePluginID = () => ""
  const emptyAppChatPage = () => undefined
  const pluginPreviewSidecarOpen = () => false
  const appChatParentSessionID = createMemo(() => {
    const value = new URLSearchParams(location.search).get("parentSessionID")?.trim()
    return value || undefined
  })
  const appChatKey = createMemo(() => `${currentDir()}:${activePluginID()}:${appChatParentSessionID() ?? ""}`)
  const creatingSession = createMemo(() => {
    if (!params.dir) return false
    if (params.id) return false
    return location.pathname.replace(/\/+$/, "").split("/").at(-1) === "session"
  })

  createEffect(() => {
    writeSmartworkReturnPath(location.pathname + location.search)
  })

  const [state, setState] = createStore({
    autoselect: !initialDirectory,
    busyWorkspaces: {} as Record<string, boolean>,
    hoverProject: undefined as string | undefined,
    scrollSessionKey: undefined as string | undefined,
    nav: undefined as HTMLElement | undefined,
    sortNow: Date.now(),
    sizing: false,
    peek: undefined as string | undefined,
    peeked: false,
  })

  const editor = createInlineEditorController()
  const setBusy = (directory: string, value: boolean) => {
    const key = pathKey(directory)
    if (value) {
      setState("busyWorkspaces", key, true)
      return
    }
    setState(
      "busyWorkspaces",
      produce((draft) => {
        delete draft[key]
      }),
    )
  }
  const isBusy = (directory: string) => !!state.busyWorkspaces[pathKey(directory)]
  const navLeave = { current: undefined as number | undefined }
  const sortNow = () => state.sortNow
  let sizet: number | undefined
  let sortNowInterval: ReturnType<typeof setInterval> | undefined
  const sortNowTimeout = setTimeout(
    () => {
      setState("sortNow", Date.now())
      sortNowInterval = setInterval(() => setState("sortNow", Date.now()), 60_000)
    },
    60_000 - (Date.now() % 60_000),
  )

  const activeProjectRoute = createMemo(() => !!currentDir())
  const sidebarPanelOpened = createMemo(() => !standaloneRoute() && activeProjectRoute() && layout.sidebar.opened())
  const sidebarRailOnly = createMemo(() => standaloneRoute())

  const aim = createAim({
    enabled: () => activeProjectRoute() && !sidebarRailOnly() && !sidebarPanelOpened(),
    active: () => state.hoverProject,
    el: () => state.nav?.querySelector<HTMLElement>("[data-component='sidebar-rail']") ?? state.nav,
    onActivate: (directory) => {
      globalSync.child(directory)
      setState("hoverProject", directory)
    },
  })

  onCleanup(() => {
    dialogDead = true
    dialogRun += 1
    if (navLeave.current !== undefined) clearTimeout(navLeave.current)
    clearTimeout(sortNowTimeout)
    if (sortNowInterval) clearInterval(sortNowInterval)
    if (sizet !== undefined) clearTimeout(sizet)
    if (peekt !== undefined) clearTimeout(peekt)
    aim.reset()
  })

  onMount(() => {
    const stop = () => setState("sizing", false)
    const blur = () => reset()
    const hide = () => {
      if (document.visibilityState !== "hidden") return
      reset()
    }
    makeEventListener(window, "pointerup", stop)
    makeEventListener(window, "pointercancel", stop)
    makeEventListener(window, "blur", stop)
    makeEventListener(window, "blur", blur)
    makeEventListener(document, "visibilitychange", hide)
  })

  const sidebarHovering = createMemo(
    () => activeProjectRoute() && !sidebarRailOnly() && !sidebarPanelOpened() && state.hoverProject !== undefined,
  )
  const sidebarExpanded = createMemo(() => sidebarPanelOpened() || sidebarHovering())

  const setHoverProject = (value: string | undefined) => {
    setState("hoverProject", value)
    if (value !== undefined) return
    aim.reset()
  }
  const clearHoverProjectSoon = () => queueMicrotask(() => setHoverProject(undefined))

  const disarm = () => {
    if (navLeave.current === undefined) return
    clearTimeout(navLeave.current)
    navLeave.current = undefined
  }

  const reset = () => {
    disarm()
    setHoverProject(undefined)
  }

  createEffect(() => {
    if (activeProjectRoute()) return
    reset()
    if (layout.sidebar.opened()) layout.sidebar.close()
    if (layout.mobileSidebar.opened()) layout.mobileSidebar.hide()
  })

  const arm = () => {
    if (sidebarPanelOpened() || sidebarRailOnly()) return
    if (state.hoverProject === undefined) return
    disarm()
    navLeave.current = window.setTimeout(() => {
      navLeave.current = undefined
      setHoverProject(undefined)
    }, 300)
  }

  let peekt: number | undefined

  const hoverProjectData = createMemo(() => {
    const id = state.hoverProject
    if (!id) return
    return layout.projects.list().find((project) => project.worktree === id)
  })

  const peekProject = createMemo(() => {
    const id = state.peek
    if (!id) return
    return layout.projects.list().find((project) => project.worktree === id)
  })

  createEffect(() => {
    const p = hoverProjectData()
    if (p) {
      if (peekt !== undefined) {
        clearTimeout(peekt)
        peekt = undefined
      }
      setState("peek", p.worktree)
      setState("peeked", true)
      return
    }

    setState("peeked", false)
    if (state.peek === undefined) return
    if (peekt !== undefined) clearTimeout(peekt)
    peekt = window.setTimeout(() => {
      peekt = undefined
      setState("peek", undefined)
    }, 180)
  })

  createEffect(() => {
    if (!sidebarPanelOpened()) return
    setHoverProject(undefined)
  })

  createEffect(() => {
    if (!state.autoselect) return
    const dir = params.dir
    if (!dir) return
    const directory = decode64(dir)
    if (!directory) return
    setState("autoselect", false)
  })

  const editorOpen = editor.editorOpen
  const openEditor = editor.openEditor
  const closeEditor = editor.closeEditor
  const setEditor = editor.setEditor
  const InlineEditor = editor.InlineEditor

  const clearSidebarHoverState = () => {
    if (sidebarPanelOpened()) return
    reset()
  }

  const navigateWithSidebarReset = (href: string) => {
    clearSidebarHoverState()
    navigate(href)
    layout.mobileSidebar.hide()
  }

  function cycleTheme(direction = 1) {
    const ids = availableThemeEntries().map(([id]) => id)
    if (ids.length === 0) return
    const currentIndex = ids.indexOf(theme.themeId())
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + direction + ids.length) % ids.length
    const nextThemeId = ids[nextIndex]
    theme.setTheme(nextThemeId)
    showToast({
      title: language.t("toast.theme.title"),
      description: theme.name(nextThemeId),
    })
  }

  function cycleColorScheme(direction = 1) {
    const current = theme.colorScheme()
    const currentIndex = colorSchemeOrder.indexOf(current)
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + direction + colorSchemeOrder.length) % colorSchemeOrder.length
    const next = colorSchemeOrder[nextIndex]
    theme.setColorScheme(next)
    showToast({
      title: language.t("toast.scheme.title"),
      description: colorSchemeLabel(next),
    })
  }

  function setLocale(next: Locale) {
    if (next === language.locale()) return
    language.setLocale(next)
    showToast({
      title: language.t("toast.language.title"),
      description: language.t("toast.language.description", { language: language.label(next) }),
    })
  }

  function cycleLanguage(direction = 1) {
    const locales = language.locales
    const currentIndex = locales.indexOf(language.locale())
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + direction + locales.length) % locales.length
    const next = locales[nextIndex]
    if (!next) return
    setLocale(next)
  }

  const useUpdatePolling = () =>
    onMount(() => {
      if (!platform.checkUpdate || !platform.updateAndRestart) return

      let toastId: number | undefined
      let interval: ReturnType<typeof setInterval> | undefined

      const pollUpdate = () =>
        platform.checkUpdate!().then(({ updateAvailable, version }) => {
          if (!updateAvailable) return
          if (toastId !== undefined) return
          toastId = showToast({
            persistent: true,
            icon: "download",
            title: language.t("toast.update.title"),
            description: language.t("toast.update.description", { version: version ?? "" }),
            actions: [
              {
                label: language.t("toast.update.action.installRestart"),
                onClick: async () => {
                  await platform.updateAndRestart!()
                },
              },
              {
                label: language.t("toast.update.action.notYet"),
                onClick: "dismiss",
              },
            ],
          })
        })

      createEffect(() => {
        if (!settings.ready()) return

        if (!settings.updates.startup()) {
          if (interval === undefined) return
          clearInterval(interval)
          interval = undefined
          return
        }

        if (interval !== undefined) return
        void pollUpdate()
        interval = setInterval(pollUpdate, 10 * 60 * 1000)
      })

      onCleanup(() => {
        if (interval === undefined) return
        clearInterval(interval)
      })
    })

  const useSDKNotificationToasts = () =>
    onMount(() => {
      const toastBySession = new Map<string, number>()
      const alertedAtBySession = new Map<string, number>()
      const cooldownMs = 5000

      const dismissSessionAlert = (sessionKey: string) => {
        const toastId = toastBySession.get(sessionKey)
        if (toastId === undefined) return
        toaster.dismiss(toastId)
        toastBySession.delete(sessionKey)
        alertedAtBySession.delete(sessionKey)
      }

      const unsub = globalSDK.event.listen((e) => {
        if (e.details?.type === "worktree.ready") {
          setBusy(e.name, false)
          WorktreeState.ready(server.scope(), e.name)
          return
        }

        if (e.details?.type === "worktree.failed") {
          setBusy(e.name, false)
          WorktreeState.failed(server.scope(), e.name, e.details.properties?.message ?? language.t("common.requestFailed"))
          return
        }

        if (
          e.details?.type === "question.replied" ||
          e.details?.type === "question.rejected" ||
          e.details?.type === "permission.replied"
        ) {
          const props = e.details.properties as { sessionID: string }
          const sessionKey = `${e.name}:${props.sessionID}`
          dismissSessionAlert(sessionKey)
          return
        }

        if (e.details?.type !== "permission.asked" && e.details?.type !== "question.asked") return
        const title =
          e.details.type === "permission.asked"
            ? language.t("notification.permission.title")
            : language.t("notification.question.title")
        const icon = e.details.type === "permission.asked" ? ("checklist" as const) : ("bubble-5" as const)
        const directory = e.name
        const props = e.details.properties
        if (e.details.type === "permission.asked" && permission.autoResponds(e.details.properties, directory)) return

        const [store] = globalSync.child(directory, { bootstrap: false })
        const session = store.session.find((s) => s.id === props.sessionID)
        const sessionKey = `${directory}:${props.sessionID}`

        const sessionTitle = session?.title ?? language.t("command.session.new")
        const projectName = getFilename(directory)
        const description =
          e.details.type === "permission.asked"
            ? language.t("notification.permission.description", { sessionTitle, projectName })
            : language.t("notification.question.description", { sessionTitle, projectName })
        const href = `/${base64Encode(directory)}/session/${props.sessionID}`

        const now = Date.now()
        const lastAlerted = alertedAtBySession.get(sessionKey) ?? 0
        if (now - lastAlerted < cooldownMs) return
        alertedAtBySession.set(sessionKey, now)

        if (e.details.type === "permission.asked") {
          if (settings.sounds.permissionsEnabled()) {
            void playSoundById(settings.sounds.permissions())
          }
          if (settings.notifications.permissions()) {
            void platform.notify(title, description, href)
          }
        }

        if (e.details.type === "question.asked") {
          if (settings.notifications.agent()) {
            void platform.notify(title, description, href)
          }
        }

        const currentSession = params.id
        if (pathKey(directory) === pathKey(currentDir()) && props.sessionID === currentSession) return
        if (pathKey(directory) === pathKey(currentDir()) && session?.parentID === currentSession) return

        dismissSessionAlert(sessionKey)

        const toastId = showToast({
          persistent: true,
          icon,
          title,
          description,
          actions: [
            {
              label: language.t("notification.action.goToSession"),
              onClick: () => navigate(href),
            },
            {
              label: language.t("common.dismiss"),
              onClick: "dismiss",
            },
          ],
        })
        toastBySession.set(sessionKey, toastId)
      })
      onCleanup(unsub)

      createEffect(() => {
        const currentSession = params.id
        if (!currentDir() || !currentSession) return
        const sessionKey = `${currentDir()}:${currentSession}`
        dismissSessionAlert(sessionKey)
        const [store] = globalSync.child(currentDir(), { bootstrap: false })
        const childSessions = store.session.filter((s) => s.parentID === currentSession)
        for (const child of childSessions) {
          dismissSessionAlert(`${currentDir()}:${child.id}`)
        }
      })
    })

  useUpdatePolling()
  useSDKNotificationToasts()

  function scrollToSession(sessionId: string, sessionKey: string) {
    if (!scrollContainerRef) return
    if (state.scrollSessionKey === sessionKey) return
    const element = scrollContainerRef.querySelector(`[data-session-id="${sessionId}"]`)
    if (!element) return
    const containerRect = scrollContainerRef.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    if (elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom) {
      setState("scrollSessionKey", sessionKey)
      return
    }
    setState("scrollSessionKey", sessionKey)
    element.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  const currentProject = createMemo(() => {
    const directory = currentDir()
    if (!directory) return
    const key = pathKey(directory)

    const projects = layout.projects.list()

    const sandbox = projects.find((p) => p.sandboxes?.some((item) => pathKey(item) === key))
    if (sandbox) return sandbox

    const direct = projects.find((p) => pathKey(p.worktree) === key)
    if (direct) return direct

    const [child] = globalSync.child(directory, { bootstrap: false })
    const id = child.project
    if (!id) return

    const meta = globalSync.data.project.find((p) => p.id === id)
    const root = meta?.worktree
    if (!root) return

    return projects.find((p) => p.worktree === root)
  })
  const smartworkNavigationProjectSlug = createMemo(() => {
    const project = currentProject()
    if (project) return base64Encode(project.worktree)
    return smartworkProjectFromLocation()?.slug ?? null
  })

  const autoselectProject = createMemo(() => {
    if (standaloneRoute()) return
    if (!state.autoselect) return
    if (!pageReady()) return
    if (!layoutReady()) return
    if (params.dir) return
    return resolveAutoselectProject(layout.projects.list(), server.projects.last())
  })
  const [autoselecting] = createResource(autoselectProject, async (directory) => {
    if (!directory) return
    setState("autoselect", false)
    await openProject(directory, true)
  })

  const workspaceName = (directory: string, projectId?: string, branch?: string) => {
    const key = pathKey(directory)
    const direct = store.workspaceName[key] ?? store.workspaceName[directory]
    if (direct) return direct
    if (!projectId) return
    if (!branch) return
    return store.workspaceBranchName[projectId]?.[branch]
  }

  const setWorkspaceName = (directory: string, next: string, projectId?: string, branch?: string) => {
    const key = pathKey(directory)
    setStore("workspaceName", key, next)
    if (!projectId) return
    if (!branch) return
    if (!store.workspaceBranchName[projectId]) {
      setStore("workspaceBranchName", projectId, {})
    }
    setStore("workspaceBranchName", projectId, branch, next)
  }

  const workspaceLabel = (directory: string, branch?: string, projectId?: string) =>
    workspaceName(directory, projectId, branch) ?? getFilename(directory)

  const workspaceSetting = createMemo(() => {
    const project = currentProject()
    if (!project) return false
    if (project.vcs !== "git") return false
    return layout.sidebar.workspaces(project.worktree)()
  })

  const visibleSessionDirs = createMemo(() => {
    const project = currentProject()
    if (!project) return [] as string[]
    if (!workspaceSetting()) return [project.worktree]

    const activeDir = currentDir()
    return workspaceIds(project).filter((directory) => {
      const expanded = store.workspaceExpanded[directory] ?? directory === project.worktree
      const active = pathKey(directory) === pathKey(activeDir)
      return expanded || active
    })
  })

  createEffect(() => {
    if (!pageReady()) return
    if (!layoutReady()) return
    const projects = layout.projects.list()
    for (const [directory, expanded] of Object.entries(store.workspaceExpanded)) {
      if (!expanded) continue
      const key = pathKey(directory)
      const project = projects.find(
        (item) => pathKey(item.worktree) === key || item.sandboxes?.some((sandbox) => pathKey(sandbox) === key),
      )
      if (!project) continue
      if (pathKey(project.worktree) === key) continue
      if (project.vcs === "git" && layout.sidebar.workspaces(project.worktree)()) continue
      setStore("workspaceExpanded", directory, false)
    }
  })

  const currentSessions = createMemo(() => {
    const now = Date.now()
    const dirs = visibleSessionDirs()
    if (dirs.length === 0) return [] as Session[]

    const result: Session[] = []
    for (const dir of dirs) {
      const [dirStore] = globalSync.child(dir, { bootstrap: true })
      const dirSessions = sortedRootSessions(dirStore, now)
      result.push(...dirSessions)
    }
    return result
  })

  type PrefetchQueue = {
    inflight: Set<string>
    pending: string[]
    pendingSet: Set<string>
    running: number
  }

  const prefetchChunk = 200
  const prefetchConcurrency = 2
  const prefetchPendingLimit = 10
  const span = 4
  const prefetchToken = { value: 0 }
  const prefetchQueues = new Map<string, PrefetchQueue>()

  const PREFETCH_MAX_SESSIONS_PER_DIR = 10
  const prefetchedByDir = new Map<string, Set<string>>()

  const lruFor = (directory: string) => {
    const existing = prefetchedByDir.get(directory)
    if (existing) return existing
    const created = new Set<string>()
    prefetchedByDir.set(directory, created)
    return created
  }

  const markPrefetched = (directory: string, sessionID: string) => {
    const lru = lruFor(directory)
    return pickSessionCacheEvictions({
      seen: lru,
      keep: sessionID,
      limit: PREFETCH_MAX_SESSIONS_PER_DIR,
      preserve: params.id && pathKey(directory) === pathKey(currentDir()) ? [params.id] : undefined,
    })
  }

  createEffect(() => {
    const active = new Set(visibleSessionDirs())
    for (const directory of prefetchedByDir.keys()) {
      if (active.has(directory)) continue
      prefetchedByDir.delete(directory)
    }
  })

  createEffect(() => {
    route()
    globalSDK.url

    prefetchToken.value += 1
    clearSessionPrefetchInflight(server.scope())
    prefetchQueues.clear()
  })

  createEffect(() => {
    const visible = new Set(visibleSessionDirs())
    for (const [directory, q] of prefetchQueues) {
      if (visible.has(directory)) continue
      q.pending.length = 0
      q.pendingSet.clear()
      if (q.running === 0) prefetchQueues.delete(directory)
    }
  })

  const queueFor = (directory: string) => {
    const existing = prefetchQueues.get(directory)
    if (existing) return existing

    const created: PrefetchQueue = {
      inflight: new Set(),
      pending: [],
      pendingSet: new Set(),
      running: 0,
    }
    prefetchQueues.set(directory, created)
    return created
  }

  const mergeByID = <T extends { id: string }>(current: T[], incoming: T[]) => {
    if (current.length === 0) {
      return incoming.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    }

    const map = new Map<string, T>()
    for (const item of current) {
      map.set(item.id, item)
    }
    for (const item of incoming) {
      map.set(item.id, item)
    }
    return [...map.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  }

  async function prefetchMessages(directory: string, sessionID: string, token: number) {
    const [store, setStore] = globalSync.child(directory, { bootstrap: false })

    return runSessionPrefetch({
      scope: server.scope(),
      directory,
      sessionID,
      task: (rev) =>
        retry(() => globalSDK.client.session.messages({ directory, sessionID, limit: prefetchChunk }))
          .then((messages) => {
            if (prefetchToken.value !== token) return
            if (!isSessionPrefetchCurrent(server.scope(), directory, sessionID, rev)) return

            const items = (messages.data ?? []).filter((x) => !!x?.info?.id)
            const next = items.map((x) => x.info).filter((m): m is Message => !!m?.id)
            const sorted = mergeByID([], next)
            const stale = markPrefetched(directory, sessionID)
            const cursor = messages.response.headers.get("x-next-cursor") ?? undefined
            const meta = {
              limit: sorted.length,
              cursor,
              complete: !cursor,
              at: Date.now(),
            }

            if (stale.length > 0) {
              clearSessionPrefetch(server.scope(), directory, stale)
              for (const id of stale) {
                globalSync.todo.set(id, undefined)
              }
            }

            const current = store.message[sessionID] ?? []
            const merged = mergeByID(
              current.filter((item): item is Message => !!item?.id),
              sorted,
            )

            if (!isSessionPrefetchCurrent(server.scope(), directory, sessionID, rev)) return

            batch(() => {
              if (stale.length > 0) {
                setStore(
                  produce((draft) => {
                    dropSessionCaches(draft, stale)
                  }),
                )
              }

              setStore("message", sessionID, reconcile(merged, { key: "id" }))
              setSessionPrefetch({ scope: server.scope(), directory, sessionID, ...meta })

              for (const message of items) {
                const currentParts = store.part[message.info.id] ?? []
                const mergedParts = mergeByID(
                  currentParts.filter((item): item is (typeof currentParts)[number] & { id: string } => !!item?.id),
                  message.parts.filter((item): item is (typeof message.parts)[number] & { id: string } => !!item?.id),
                )

                setStore("part", message.info.id, reconcile(mergedParts, { key: "id" }))
              }
            })

            return meta
          })
          .catch(() => undefined),
    })
  }

  const pumpPrefetch = (directory: string) => {
    const q = queueFor(directory)
    if (q.running >= prefetchConcurrency) return

    const sessionID = q.pending.shift()
    if (!sessionID) return

    q.pendingSet.delete(sessionID)
    q.inflight.add(sessionID)
    q.running += 1

    const token = prefetchToken.value

    void prefetchMessages(directory, sessionID, token).finally(() => {
      q.running -= 1
      q.inflight.delete(sessionID)
      pumpPrefetch(directory)
    })
  }

  const prefetchSession = (session: Session, priority: "high" | "low" = "low") => {
    const directory = session.directory
    if (!directory) return

    const [store] = globalSync.child(directory, { bootstrap: false })
    const cached = untrack(() => {
      const info = getSessionPrefetch(server.scope(), directory, session.id)
      return shouldSkipSessionPrefetch({
        message: store.message[session.id] !== undefined,
        info,
        chunk: prefetchChunk,
      })
    })
    if (cached) return

    const q = queueFor(directory)
    if (q.inflight.has(session.id)) return
    if (q.pendingSet.has(session.id)) {
      if (priority !== "high") return
      const index = q.pending.indexOf(session.id)
      if (index > 0) {
        q.pending.splice(index, 1)
        q.pending.unshift(session.id)
      }
      return
    }

    const lru = lruFor(directory)
    const known = lru.has(session.id)
    if (!known && lru.size >= PREFETCH_MAX_SESSIONS_PER_DIR && priority !== "high") return

    if (priority === "high") q.pending.unshift(session.id)
    if (priority !== "high") q.pending.push(session.id)
    q.pendingSet.add(session.id)

    while (q.pending.length > prefetchPendingLimit) {
      const dropped = q.pending.pop()
      if (!dropped) continue
      q.pendingSet.delete(dropped)
    }

    pumpPrefetch(directory)
  }

  const warm = (sessions: Session[], index: number) => {
    for (let offset = 1; offset <= span; offset++) {
      const next = sessions[index + offset]
      if (next) prefetchSession(next, offset === 1 ? "high" : "low")

      const prev = sessions[index - offset]
      if (prev) prefetchSession(prev, offset === 1 ? "high" : "low")
    }
  }

  createEffect(() => {
    const sessions = currentSessions()
    if (sessions.length === 0) return

    const index = params.id ? sessions.findIndex((s) => s.id === params.id) : 0
    if (index === -1) return

    if (!params.id) {
      const first = sessions[index]
      if (first) prefetchSession(first, "high")
    }

    warm(sessions, index)
  })

  function navigateSessionByOffset(offset: number) {
    const sessions = currentSessions()
    if (sessions.length === 0) return

    const sessionIndex = params.id ? sessions.findIndex((s) => s.id === params.id) : -1

    let targetIndex: number
    if (sessionIndex === -1) {
      targetIndex = offset > 0 ? 0 : sessions.length - 1
    } else {
      targetIndex = (sessionIndex + offset + sessions.length) % sessions.length
    }

    const session = sessions[targetIndex]
    if (!session) return

    prefetchSession(session, "high")
    warm(sessions, targetIndex)

    navigateToSession(session)
  }

  function navigateProjectByOffset(offset: number) {
    const projects = layout.projects.list()
    if (projects.length === 0) return

    const current = currentProject()?.worktree
    const fallback = currentDir() ? projectRoot(currentDir()) : undefined
    const active = current ?? fallback
    const index = active ? projects.findIndex((project) => project.worktree === active) : -1

    const target =
      index === -1
        ? offset > 0
          ? projects[0]
          : projects[projects.length - 1]
        : projects[(index + offset + projects.length) % projects.length]
    if (!target) return

    // warm up child store to prevent flicker
    globalSync.child(target.worktree)
    void openProject(target.worktree)
  }

  function navigateSessionByUnseen(offset: number) {
    const sessions = currentSessions()
    if (sessions.length === 0) return

    const hasUnseen = sessions.some((session) => notification.session.unseenCount(session.id) > 0)
    if (!hasUnseen) return

    const activeIndex = params.id ? sessions.findIndex((s) => s.id === params.id) : -1
    const start = activeIndex === -1 ? (offset > 0 ? -1 : 0) : activeIndex

    for (let i = 1; i <= sessions.length; i++) {
      const index = offset > 0 ? (start + i) % sessions.length : (start - i + sessions.length) % sessions.length
      const session = sessions[index]
      if (!session) continue
      if (notification.session.unseenCount(session.id) === 0) continue

      prefetchSession(session, "high")
      warm(sessions, index)

      navigateToSession(session)
      return
    }
  }

  async function archiveSession(session: Session) {
    const [store, setStore] = globalSync.child(session.directory)
    const sessions = store.session ?? []
    const index = sessions.findIndex((s) => s.id === session.id)
    const nextSession = sessions[index + 1] ?? sessions[index - 1]

    await globalSDK.client.session.update({
      directory: session.directory,
      sessionID: session.id,
      time: { archived: Date.now() },
    })
    setStore(
      produce((draft) => {
        const match = Binary.search(draft.session, session.id, (s) => s.id)
        if (match.found) draft.session.splice(match.index, 1)
      }),
    )
    if (session.id === params.id) {
      if (nextSession) {
        navigate(`/${params.dir}/session/${nextSession.id}`)
      } else {
        navigate(`/${params.dir}/session`)
      }
    }
  }

  async function renameSessionTitle(session: Session, title: string) {
    await globalSDK.client.session
      .update({
        directory: session.directory,
        sessionID: session.id,
        title,
      })
      .then(() => {
        const [, setStore] = globalSync.child(session.directory)
        setStore(
          produce((draft) => {
            const match = Binary.search(draft.session, session.id, (s) => s.id)
            if (match.found) draft.session[match.index].title = title
          }),
        )
      })
      .catch((err) => {
        console.error("[layout] session rename failed", err)
        showToast({
          title: "Session title could not be saved",
          description: layoutSessionRenameFailedMessage(),
        })
      })
  }

  command.register("layout", () => {
    const commands: CommandOption[] = [
      {
        id: "sidebar.toggle",
        title: language.t("command.sidebar.toggle"),
        category: language.t("command.category.view"),
        keybind: "mod+b",
        disabled: !activeProjectRoute(),
        onSelect: () => {
          if (!activeProjectRoute()) return
          layout.sidebar.toggle()
        },
      },
      {
        id: "project.open",
        title: language.t("command.project.open"),
        category: language.t("command.category.project"),
        keybind: "mod+o",
        onSelect: () => chooseProject(),
      },
      {
        id: "project.previous",
        title: language.t("command.project.previous"),
        category: language.t("command.category.project"),
        keybind: "mod+alt+arrowup",
        onSelect: () => navigateProjectByOffset(-1),
      },
      {
        id: "project.next",
        title: language.t("command.project.next"),
        category: language.t("command.category.project"),
        keybind: "mod+alt+arrowdown",
        onSelect: () => navigateProjectByOffset(1),
      },
      {
        id: "provider.connect",
        title: language.t("command.provider.connect"),
        category: language.t("command.category.provider"),
        onSelect: () => connectProvider(),
      },
      {
        id: "server.switch",
        title: language.t("command.server.switch"),
        category: language.t("command.category.server"),
        onSelect: () => openServer(),
      },
      {
        id: "settings.open",
        title: language.t("command.settings.open"),
        category: language.t("command.category.settings"),
        keybind: "mod+comma",
        onSelect: () => openSettings(),
      },
      {
        id: "settings.shortcuts.open",
        title: language.t("settings.tab.shortcuts"),
        category: language.t("command.category.settings"),
        keybind: "mod+/",
        onSelect: () => openSettings("shortcuts"),
      },
      {
        id: "settings.brandGuidelines.open",
        title: "Open Brand Guidelines",
        category: language.t("command.category.settings"),
        onSelect: () => openSettings("brand-guidelines"),
      },
      {
        id: "smartwork.open",
        title: "Smartwork",
        category: language.t("command.category.view"),
        onSelect: () => navigateWithSidebarReset("/smartwork"),
      },
      {
        id: "session.previous",
        title: language.t("command.session.previous"),
        category: language.t("command.category.session"),
        keybind: "alt+arrowup",
        onSelect: () => navigateSessionByOffset(-1),
      },
      {
        id: "session.next",
        title: language.t("command.session.next"),
        category: language.t("command.category.session"),
        keybind: "alt+arrowdown",
        onSelect: () => navigateSessionByOffset(1),
      },
      {
        id: "session.previous.unseen",
        title: language.t("command.session.previous.unseen"),
        category: language.t("command.category.session"),
        keybind: "shift+alt+arrowup",
        onSelect: () => navigateSessionByUnseen(-1),
      },
      {
        id: "session.next.unseen",
        title: language.t("command.session.next.unseen"),
        category: language.t("command.category.session"),
        keybind: "shift+alt+arrowdown",
        onSelect: () => navigateSessionByUnseen(1),
      },
      {
        id: "session.archive",
        title: language.t("command.session.archive"),
        category: language.t("command.category.session"),
        keybind: "mod+shift+backspace",
        disabled: !params.dir || !params.id,
        onSelect: () => {
          const session = currentSessions().find((s) => s.id === params.id)
          if (session) void archiveSession(session)
        },
      },
      {
        id: "workspace.new",
        title: language.t("workspace.new"),
        category: language.t("command.category.workspace"),
        keybind: "mod+shift+w",
        disabled: !workspaceSetting(),
        onSelect: () => {
          const project = currentProject()
          if (!project) return
          return createWorkspace(project)
        },
      },
      {
        id: "workspace.toggle",
        title: language.t("command.workspace.toggle"),
        description: language.t("command.workspace.toggle.description"),
        category: language.t("command.category.workspace"),
        slash: "workspace",
        disabled: !currentProject(),
        onSelect: () => {
          const project = currentProject()
          if (!project) return
          return toggleProjectWorkspaces(project)
        },
      },
      {
        id: "theme.cycle",
        title: language.t("command.theme.cycle"),
        category: language.t("command.category.theme"),
        keybind: "mod+shift+t",
        onSelect: () => cycleTheme(1),
      },
    ]

    for (const [id] of availableThemeEntries()) {
      commands.push({
        id: `theme.set.${id}`,
        title: language.t("command.theme.set", { theme: theme.name(id) }),
        category: language.t("command.category.theme"),
        onSelect: () => theme.commitPreview(),
        onHighlight: () => {
          theme.previewTheme(id)
          return () => theme.cancelPreview()
        },
      })
    }

    commands.push({
      id: "theme.scheme.cycle",
      title: language.t("command.theme.scheme.cycle"),
      category: language.t("command.category.theme"),
      keybind: "mod+shift+s",
      onSelect: () => cycleColorScheme(1),
    })

    for (const scheme of colorSchemeOrder) {
      commands.push({
        id: `theme.scheme.${scheme}`,
        title: language.t("command.theme.scheme.set", { scheme: colorSchemeLabel(scheme) }),
        category: language.t("command.category.theme"),
        onSelect: () => theme.commitPreview(),
        onHighlight: () => {
          theme.previewColorScheme(scheme)
          return () => theme.cancelPreview()
        },
      })
    }

    commands.push({
      id: "language.cycle",
      title: language.t("command.language.cycle"),
      category: language.t("command.category.language"),
      onSelect: () => cycleLanguage(1),
    })

    for (const locale of language.locales) {
      commands.push({
        id: `language.set.${locale}`,
        title: language.t("command.language.set", { language: language.label(locale) }),
        category: language.t("command.category.language"),
        onSelect: () => setLocale(locale),
      })
    }

    return commands
  })

  function connectProvider() {
    const run = ++dialogRun
    if (dialogDead || dialogRun !== run) return
    navigateWithSidebarReset(smartworkViewPath("integrations", smartworkNavigationProjectSlug()))
  }

  function openServer() {
    const run = ++dialogRun
    void import("@/components/dialog-select-server").then((x) => {
      if (dialogDead || dialogRun !== run) return
      dialog.show(() => <x.DialogSelectServer />)
    })
  }

  function openSettings(initialTab?: SettingsTab, onOpened?: () => void) {
    const run = ++dialogRun
    void import("@/components/settings-v2").then((x) => {
      if (dialogDead || dialogRun !== run) return
      dialog.show(() => (
        <x.DialogSettings initialTab={initialTab} restartPath={currentRoutePath} directory={sidebarAppDirectory} />
      ))
      onOpened?.()
    })
  }

  let restartIntentDialogHandled = false
  createEffect(() => {
    if (restartIntentDialogHandled) return
    const intent = readPluginRestartIntent()
    if (!intent || currentRoutePath() !== intent.path) return
    restartIntentDialogHandled = true
    openSettings("plugins", () => clearPluginRestartIntent())
  })

  function projectRoot(directory: string) {
    const key = pathKey(directory)
    const project = layout.projects
      .list()
      .find((item) => pathKey(item.worktree) === key || item.sandboxes?.some((sandbox) => pathKey(sandbox) === key))
    if (project) return project.worktree

    const known = Object.entries(store.workspaceOrder).find(
      ([root, dirs]) => pathKey(root) === key || dirs.some((item) => pathKey(item) === key),
    )
    if (known) return known[0]

    const [child] = globalSync.child(directory, { bootstrap: false })
    const id = child.project
    if (!id) return directory

    const meta = globalSync.data.project.find((item) => item.id === id)
    return meta?.worktree ?? directory
  }

  function activeProjectRoot(directory: string) {
    return currentProject()?.worktree ?? projectRoot(directory)
  }

  function rememberSessionRoute(directory: string, id: string, root = activeProjectRoot(directory)) {
    setStore("lastProjectSession", root, { directory, id, at: Date.now() })
    return root
  }

  function clearLastProjectSession(root: string) {
    if (!store.lastProjectSession[root]) return
    setStore(
      "lastProjectSession",
      produce((draft) => {
        delete draft[root]
      }),
    )
  }

  function syncSessionRoute(directory: string, id: string, root = activeProjectRoot(directory)) {
    rememberSessionRoute(directory, id, root)
    notification.session.markViewed(id)
    const expanded = untrack(() => store.workspaceExpanded[directory])
    if (expanded === false) {
      setStore("workspaceExpanded", directory, true)
    }
    requestAnimationFrame(() => scrollToSession(id, `${directory}:${id}`))
    return root
  }

  async function navigateToProject(directory: string | undefined) {
    if (!directory) return
    const root = projectRoot(directory)
    server.projects.touch(root)
    const project = layout.projects.list().find((item) => item.worktree === root)
    let dirs = project
      ? effectiveWorkspaceOrder(root, [root, ...(project.sandboxes ?? [])], store.workspaceOrder[root])
      : [root]
    const canOpen = (value: string | undefined) => {
      if (!value) return false
      return dirs.some((item) => pathKey(item) === pathKey(value))
    }
    const refreshDirs = async (target?: string) => {
      if (!target || target === root || canOpen(target)) return canOpen(target)
      const listed = await globalSDK.client.worktree
        .list({ directory: root })
        .then((x) => x.data ?? [])
        .catch(() => [] as string[])
      dirs = effectiveWorkspaceOrder(root, [root, ...listed], store.workspaceOrder[root])
      return canOpen(target)
    }
    const openSession = async (target: { directory: string; id: string }) => {
      if (!canOpen(target.directory)) return false
      const [data] = globalSync.child(target.directory, { bootstrap: false })
      if (data.session.some((item) => item.id === target.id)) {
        setStore("lastProjectSession", root, { directory: target.directory, id: target.id, at: Date.now() })
        navigateWithSidebarReset(`/${base64Encode(target.directory)}/session/${target.id}`)
        return true
      }
      const resolved = await globalSDK.client.session
        .get({ sessionID: target.id })
        .then((x) => x.data)
        .catch(() => undefined)
      if (!resolved?.directory) return false
      if (!canOpen(resolved.directory)) return false
      setStore("lastProjectSession", root, { directory: resolved.directory, id: resolved.id, at: Date.now() })
      navigateWithSidebarReset(`/${base64Encode(resolved.directory)}/session/${resolved.id}`)
      return true
    }

    const projectSession = store.lastProjectSession[root]
    if (projectSession?.id) {
      await refreshDirs(projectSession.directory)
      const opened = await openSession(projectSession)
      if (opened) return
      clearLastProjectSession(root)
    }

    const latest = latestRootSession(
      dirs.map((item) => globalSync.child(item, { bootstrap: false })[0]),
      Date.now(),
    )
    if (latest && (await openSession(latest))) {
      return
    }

    const fetched = latestRootSession(
      await Promise.all(
        dirs.map(async (item) => ({
          path: { directory: item },
          session: await globalSDK.client.session
            .list({ directory: item })
            .then((x) => x.data ?? [])
            .catch(() => []),
        })),
      ),
      Date.now(),
    )
    if (fetched && (await openSession(fetched))) {
      return
    }

    navigateWithSidebarReset(`/${base64Encode(root)}/session`)
  }

  function navigateToSession(session: Session | undefined) {
    if (!session) return
    navigateWithSidebarReset(`/${base64Encode(session.directory)}/session/${session.id}`)
  }

  function openProject(directory: string, shouldNavigate: boolean | string = true) {
    if (rejectFilesystemRootProject(directory)) return
    layout.projects.open(directory)
    if (typeof shouldNavigate === "string") {
      server.projects.touch(projectRoot(directory))
      navigateWithSidebarReset(shouldNavigate)
      return
    }
    if (shouldNavigate) return navigateToProject(directory)
  }

  const resolveDeepLinkDirectory = (
    link: OpenProjectDeepLink | NewSessionDeepLink,
    action: MissingLocalProjectDeepLinkAction,
  ) => {
    const resolution = resolveProjectDeepLinkDirectory(link, resolveSmartworkWorkspaceResolverDetailed)
    if (!resolution) return null
    if (resolution.kind === "directory") return resolution.directory
    if (resolution.kind === "ambiguous-local-project") {
      // Phase 1 lands the signal; Phase 2 will replace this with a dedicated picker.
      // For now, route to the missing-local-project flow with the same workspaceId so the user
      // gets the existing project picker instead of a silent null.
      navigateWithSidebarReset(
        missingLocalProjectDeepLinkPath({
          workspaceId: resolution.workspaceId,
          desktopId: resolution.desktopId,
          handle: resolution.handle,
          action,
          ...("prompt" in link && link.prompt ? { prompt: link.prompt } : {}),
          ...(link.smartwork ? { smartwork: true } : {}),
        }),
      )
      return null
    }
    navigateWithSidebarReset(
      missingLocalProjectDeepLinkPath({
        workspaceId: resolution.workspaceId,
        desktopId: resolution.desktopId,
        handle: resolution.handle,
        action,
        ...("prompt" in link && link.prompt ? { prompt: link.prompt } : {}),
        ...(link.smartwork ? { smartwork: true } : {}),
      }),
    )
    return null
  }

  const handleDeepLinks = (urls: string[]) => {
    for (const link of collectBillingDeepLinks(urls)) {
      navigateWithSidebarReset(billingDeepLinkPath(link))
    }

    if (!server.isLocal()) return

    for (const link of collectOpenProjectDeepLinks(urls)) {
      const directory = resolveDeepLinkDirectory(link, "open-project")
      if (!directory) continue
      if (!link.smartwork) {
        void openProject(directory)
        continue
      }
      void openProject(directory, false)
      navigateWithSidebarReset(`/${base64Encode(directory)}/session${sessionDeepLinkSearch(link)}`)
    }

    for (const link of collectNewSessionDeepLinks(urls)) {
      const directory = resolveDeepLinkDirectory(link, "new-session")
      if (!directory) continue
      void openProject(directory, false)
      const slug = base64Encode(directory)
      if (link.prompt) {
        setSessionHandoff(slug, { prompt: link.prompt })
      }
      navigateWithSidebarReset(`/${slug}/session${sessionDeepLinkSearch(link)}`)
    }
  }

  const canHandleLayoutDeepLink = (url: string) =>
    !!parseDeepLink(url) || !!parseNewSessionDeepLink(url) || !!parseBillingDeepLink(url)

  onMount(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ urls: string[] }>).detail
      const urls = detail?.urls ?? []
      if (urls.length === 0) return
      handleDeepLinks(urls)
    }

    handleDeepLinks(drainPendingDeepLinks(window, canHandleLayoutDeepLink))
    makeEventListener(window, deepLinkEvent, handler as EventListener)
  })

  async function renameProject(project: LocalProject, next: string) {
    const current = displayName(project)
    if (next === current) return
    const name = next === getFilename(project.worktree) ? "" : next

    if (project.id && project.id !== "global") {
      await globalSDK.client.project.update({ projectID: project.id, directory: project.worktree, name })
      return
    }

    globalSync.project.meta(project.worktree, { name })
  }

  const renameWorkspace = (directory: string, next: string, projectId?: string, branch?: string) => {
    const current = workspaceName(directory, projectId, branch) ?? getFilename(directory)
    if (current === next) return
    setWorkspaceName(directory, next, projectId, branch)
  }

  function closeProject(directory: string) {
    const list = layout.projects.list()
    const key = pathKey(directory)
    const index = list.findIndex((x) => pathKey(x.worktree) === key)
    const active = pathKey(currentProject()?.worktree ?? "") === key
    if (index === -1) return
    const next = list[index + 1]

    if (!active) {
      layout.projects.close(directory)
      return
    }

    if (!next) {
      layout.projects.close(directory)
      navigate("/")
      return
    }

    navigateWithSidebarReset(`/${base64Encode(next.worktree)}/session`)
    layout.projects.close(directory)
    queueMicrotask(() => {
      void navigateToProject(next.worktree)
    })
  }

  async function toggleProjectWorkspaces(project: LocalProject) {
    if (workspaceTogglePending.has(project.worktree)) return
    workspaceTogglePending.add(project.worktree)
    try {
      const result = await toggleProjectWorkspaceSetting({
        project,
        enabled: layout.sidebar.workspaces(project.worktree)(),
        initializeGit: async (directory) => {
          const response = await globalSDK.client.project.initGit({ directory })
          if (!response.data) throw new Error("Git initialization returned no project")
          globalSync.set("project", upsertInitializedProject(globalSync.data.project, response.data))
        },
        createWorkspace: async (directory) => {
          const initialized = globalSync.data.project.find((item) => pathKey(item.worktree) === pathKey(directory))
          if (!initialized) return false
          return Boolean(await createWorkspace({ ...project, ...initialized }))
        },
        toggle: layout.sidebar.toggleWorkspaces,
      })
      if (result === "creation_failed") return
      showToast({
        title: language.t(`toast.workspace.${result}.title`),
        description: language.t(`toast.workspace.${result}.description`),
      })
    } catch (error) {
      console.error("[layout] workspace git init failed", error)
      showToast({
        variant: "error",
        title: "Git could not be initialized",
        description: layoutWorkspaceGitInitFailedMessage(),
      })
    } finally {
      workspaceTogglePending.delete(project.worktree)
    }
  }

  const reviewSmartworkAccess = (project: LocalProject) =>
    navigateWithSidebarReset(smartworkCloudReviewPath(base64Encode(project.worktree)))

  const showEditProjectDialog = (project: LocalProject) => {
    const currentServer = server.current
    if (!currentServer) return
    const run = ++dialogRun
    void import("@/components/dialog-edit-project").then((x) => {
      if (dialogDead || dialogRun !== run) return
      dialog.show(() => <x.DialogEditProject project={project} server={currentServer} />)
    })
  }

  const showArchivedChatsDialog = (project: LocalProject) => {
    const run = ++dialogRun
    void import("@/components/dialog-archived-chats").then((x) => {
      if (dialogDead || dialogRun !== run) return
      dialog.show(() => (
        <x.DialogArchivedChats
          project={project}
          projectLabel={resolveProjectViewFor(project).label}
          client={{
            list: (input) => globalSDK.client.experimental.session.list(input),
            update: (input) => globalSDK.client.session.update(input),
          }}
          onClose={() => dialog.close()}
          onOpen={(session) => navigateWithSidebarReset(`/${base64Encode(session.directory)}/session/${session.id}`)}
          onUnarchived={(session) => {
            const [, setProjectStore] = globalSync.child(session.directory, { bootstrap: false })
            setProjectStore("session", (sessions) => upsertActiveSession(sessions, session))
          }}
        />
      ))
    })
  }

  async function createProjectFromTemplate(templateId: ProjectStarterTemplateID) {
    const result = await platform
      .createStarterProject?.({ templateId, rootDirectory: starterProjectRootPath() })
      .catch((err) => {
        console.error("[layout] starter project create failed", err)
        const raw = err instanceof Error ? err.message : ""
        showToast({
          title: language.t("project.start.createFailed.title"),
          description: isUnknownStarterProjectTemplateError(raw)
            ? layoutStarterProjectUnsupportedTemplateMessage()
            : layoutStarterProjectCreateFailedMessage(starterProjectRootLabel()),
        })
        return undefined
      })
    if (!result?.directory) return
    // Open the new starter as a normal workspace folder (empty state), not the
    // Smartwork app — its files/plugins are already on disk. (default navigate.)
    void openProject(result.directory, starterProjectFirstRunPath(result.directory, templateId))
  }

  // Create an EMPTY project folder — the "Create Folder" and "Custom Template" outcomes of
  // the new-project dialog. Unlike createProjectFromTemplate this copies no starter tree;
  // allowAppIDs (when non-empty) limits which bundled apps materialize into it.
  async function createBlankProject(input: { name?: string; rootDirectory?: string; allowAppIDs?: string[] }) {
    const result = await platform
      .createProjectDirectory?.({
        name: input.name,
        rootDirectory: input.rootDirectory ?? starterProjectRootPath(),
        allowAppIDs: input.allowAppIDs,
      })
      .catch((err) => {
        console.error("[layout] project folder creation failed", err)
        showToast({
          title: language.t("project.start.createFailed.title"),
          description: layoutStarterProjectCreateFailedMessage(input.rootDirectory ?? starterProjectRootLabel()),
        })
        return undefined
      })
    if (!result?.directory) return
    void openProject(result.directory)
  }

  function openNewProjectDialog() {
    // Web/hosted has no local folder creation — fall straight through to the old behavior.
    if (!platform.createProjectDirectory) {
      void chooseCustomProject()
      return
    }
    dialog.show(() => (
      <DialogNewProject
        rootLabel={starterProjectRootLabel()}
        onBack={chooseProject}
        onCreateFolder={(name) => createBlankProject({ name })}
        onBrowseFolder={chooseCustomProject}
        onCustomTemplate={(name) =>
          dialog.show(() => (
            <DialogCustomTemplate
              initialName={name}
              rootLabel={starterProjectRootLabel()}
              onChooseLocation={platform.openDirectoryPickerDialog ? pickProjectLocation : undefined}
              onSubmit={(selection) => createBlankProject(selection)}
            />
          ))
        }
      />
    ))
  }

  // Pick a project location, guarded. Every caller that lets the user choose where a
  // project lands goes through here — the filesystem root must never become a project
  // root, and creating one there would also register "/" in the starter-roots registry
  // that handle-projects walks on launch.
  async function pickProjectLocation() {
    const result = await platform.openDirectoryPickerDialog?.({
      title: language.t("project.start.root.choose"),
      multiple: false,
      defaultPath: starterProjectRootPath(),
    })
    const directory = Array.isArray(result) ? result[0] : result
    if (!directory) return undefined
    if (rejectFilesystemRootProject(directory)) return undefined
    return directory
  }

  async function chooseStarterProjectRoot() {
    const directory = await pickProjectLocation()
    if (!directory) return
    settings.general.setStarterProjectRoot(directory)
  }

  function showHostedProjectDialog(onSuccess: (directory: string) => void, onDismiss?: () => void) {
    const run = ++dialogRun
    void import("@/components/dialog-create-hosted-project").then((x) => {
      if (dialogDead || dialogRun !== run) return
      dialog.show(() => <x.DialogCreateHostedProject onSuccess={onSuccess} />, onDismiss)
    })
  }

  async function chooseHostedCustomProject(resolve: (result: string | string[] | null) => void) {
    const current = server.current
    if (!current) {
      showToast({
        title: language.t("project.start.createFailed.title"),
        description: layoutHostedProjectUploadFailedMessage(),
        variant: "error",
      })
      return
    }
    const selection = await pickHostedFolderWithNativePicker().catch((err) => {
      console.error("[layout] hosted folder picker failed", err)
      showToast({
        title: language.t("project.start.createFailed.title"),
        description: layoutHostedFolderPickerFailedMessage(),
        variant: "error",
      })
      return undefined
    })
    if (!selection) return

    showToast({
      title: language.t("project.hosted.upload.toast.uploading.title"),
      description: language.t("project.hosted.create.selectedFolder", {
        name: selection.name || language.t("project.hosted.create.selectedFolderFallback"),
        count: String(selection.files.length),
      }),
      variant: "loading",
      duration: 3000,
    })
    const result = await createHostedCustomProject({
      serverUrl: current.http.url,
      selection,
      fetch: server.isLocal() ? globalThis.fetch.bind(globalThis) : (platform.fetch ?? globalThis.fetch.bind(globalThis)),
      validateRuntimeContract: !server.isLocal(),
    }).catch((err) => {
      console.error("[layout] hosted project upload failed", err)
      showToast({
        title: language.t("project.hosted.upload.toast.failed.title"),
        description: layoutHostedProjectUploadFailedMessage(),
        variant: "error",
      })
      return undefined
    })
    if (!result) return

    showToast({
      title: language.t("project.hosted.upload.toast.success.title"),
      description: language.t("project.hosted.upload.toast.success.description", {
        name: result.name,
        count: String(result.uploadedFiles),
      }),
      variant: "success",
    })
    resolve(result.directory)
  }

  async function chooseCustomProject() {
    function resolve(result: string | string[] | null) {
      if (Array.isArray(result)) {
        const directories = result.filter((directory) => !rejectFilesystemRootProject(directory))
        for (const directory of directories) {
          void openProject(directory, false)
        }
        if (directories[0]) void navigateToProject(directories[0])
      } else if (result) {
        void openProject(result)
      }
    }

    if (platform.openDirectoryPickerDialog && server.isLocal()) {
      const result = await platform.openDirectoryPickerDialog?.({
        title: language.t("command.project.open"),
        multiple: true,
      })
      resolve(result)
    } else if (platform.platform === "web") {
      if (hostedDirectoryPickerAvailable()) {
        await chooseHostedCustomProject(resolve)
        return
      }
      showHostedProjectDialog(resolve, () => resolve(null))
    } else {
      const run = ++dialogRun
      void import("@/components/dialog-select-directory").then((x) => {
        if (dialogDead || dialogRun !== run) return
        dialog.show(
          () => <x.DialogSelectDirectory multiple={true} onSelect={resolve} />,
          () => resolve(null),
        )
      })
    }
  }

  function chooseProject() {
    if (!platform.createStarterProject) {
      if (platform.platform === "web" || !server.isLocal()) {
        showHostedProjectDialog((directory) => void openProject(directory))
        return
      }
      void chooseCustomProject()
      return
    }
    dialog.show(() => (
      <DialogProjectStart
        onCustomProject={openNewProjectDialog}
        onStarterProject={createProjectFromTemplate}
        starterProjectRoot={platform.platform === "web" ? "OpenAnalyst Cloud" : starterProjectRootLabel()}
        onChangeStarterProjectRoot={platform.openDirectoryPickerDialog ? chooseStarterProjectRoot : undefined}
      />
    ))
  }

  const deleteWorkspace = async (root: string, directory: string, leaveDeletedWorkspace = false) => {
    if (directory === root) return

    const current = currentDir()
    const currentKey = pathKey(current)
    const deletedKey = pathKey(directory)
    const shouldLeave = leaveDeletedWorkspace || (!!params.dir && currentKey === deletedKey)
    if (!leaveDeletedWorkspace && shouldLeave) {
      navigateWithSidebarReset(`/${base64Encode(root)}/session`)
    }

    setBusy(directory, true)

    // Release the workspace's running instance (and any terminals) BEFORE removing
    // it. Otherwise the sidecar still holds the worktree directory open — on
    // Windows that locks files so `git worktree remove` / the recursive rm fail
    // and delete surfaces an error toast. resetWorkspace already does this; delete
    // must too.
    const sessions: Session[] = await globalSDK.client.session
      .list({ directory })
      .then((x) => x.data ?? [])
      .catch(() => [])
    clearWorkspaceTerminals(
      directory,
      sessions.map((s) => s.id),
      platform,
    )
    await globalSDK.client.instance.dispose({ directory }).catch(() => undefined)

    const result = await globalSDK.client.worktree
      .remove({ directory: root, worktreeRemoveInput: { directory } })
      .then((x) => x.data)
      .catch((err) => {
        console.error("[layout] workspace delete failed", err)
        showToast({
          title: language.t("workspace.delete.failed.title"),
          description: layoutWorkspaceDeleteFailedMessage(),
        })
        return false
      })

    setBusy(directory, false)

    if (!result) return

    if (pathKey(store.lastProjectSession[root]?.directory ?? "") === pathKey(directory)) {
      clearLastProjectSession(root)
    }

    globalSync.set(
      "project",
      produce((draft) => {
        const project = draft.find((item) => item.worktree === root)
        if (!project) return
        project.sandboxes = (project.sandboxes ?? []).filter((sandbox) => sandbox !== directory)
      }),
    )
    setStore("workspaceOrder", root, (order) => (order ?? []).filter((workspace) => workspace !== directory))

    layout.projects.close(directory)
    layout.projects.open(root)

    if (shouldLeave) return

    const nextCurrent = currentDir()
    const nextKey = pathKey(nextCurrent)
    const project = layout.projects.list().find((item) => item.worktree === root)
    const dirs = project
      ? effectiveWorkspaceOrder(root, [root, ...(project.sandboxes ?? [])], store.workspaceOrder[root])
      : [root]
    const valid = dirs.some((item) => pathKey(item) === nextKey)

    if (params.dir && projectRoot(nextCurrent) === root && !valid) {
      navigateWithSidebarReset(`/${base64Encode(root)}/session`)
    }
  }

  const resetWorkspace = async (root: string, directory: string) => {
    if (directory === root) return
    setBusy(directory, true)

    const progress = showToast({
      persistent: true,
      title: language.t("workspace.resetting.title"),
      description: language.t("workspace.resetting.description"),
    })
    const dismiss = () => toaster.dismiss(progress)

    const sessions: Session[] = await globalSDK.client.session
      .list({ directory })
      .then((x) => x.data ?? [])
      .catch(() => [])

    clearWorkspaceTerminals(
      directory,
      sessions.map((s) => s.id),
      platform,
    )
    await globalSDK.client.instance.dispose({ directory }).catch(() => undefined)

    const result = await globalSDK.client.worktree
      .reset({ directory: root, worktreeResetInput: { directory } })
      .then((x) => x.data)
      .catch((err) => {
        console.error("[layout] workspace reset failed", err)
        showToast({
          title: language.t("workspace.reset.failed.title"),
          description: layoutWorkspaceResetFailedMessage(),
        })
        return false
      })

    if (!result) {
      setBusy(directory, false)
      dismiss()
      return
    }

    const archivedAt = Date.now()
    await Promise.all(
      sessions
        .filter((session) => session.time.archived === undefined)
        .map((session) =>
          globalSDK.client.session
            .update({
              sessionID: session.id,
              directory: session.directory,
              time: { archived: archivedAt },
            })
            .catch(() => undefined),
        ),
    )

    setBusy(directory, false)
    dismiss()

    showToast({
      title: language.t("workspace.reset.success.title"),
      description: language.t("workspace.reset.success.description"),
      actions: [
        {
          label: language.t("command.session.new"),
          onClick: () => {
            const href = `/${base64Encode(directory)}/session`
            navigate(href)
            layout.mobileSidebar.hide()
          },
        },
        {
          label: language.t("common.dismiss"),
          onClick: "dismiss",
        },
      ],
    })
  }

  function DialogDeleteWorkspace(props: { root: string; directory: string }) {
    const name = createMemo(() => getFilename(props.directory))
    const [data, setData] = createStore({
      status: "loading" as "loading" | "ready" | "error",
      dirty: false,
    })

    onMount(() => {
      globalSDK.client.file
        .status({ directory: props.directory })
        .then((x) => {
          const files = x.data ?? []
          const dirty = files.length > 0
          setData({ status: "ready", dirty })
        })
        .catch(() => {
          setData({ status: "error", dirty: false })
        })
    })

    const handleDelete = () => {
      const leaveDeletedWorkspace = !!params.dir && pathKey(currentDir()) === pathKey(props.directory)
      if (leaveDeletedWorkspace) {
        navigateWithSidebarReset(`/${base64Encode(props.root)}/session`)
      }
      dialog.close()
      void deleteWorkspace(props.root, props.directory, leaveDeletedWorkspace)
    }

    const description = () => {
      if (data.status === "loading") return language.t("workspace.status.checking")
      if (data.status === "error") return language.t("workspace.status.error")
      if (!data.dirty) return language.t("workspace.status.clean")
      return language.t("workspace.status.dirty")
    }

    return (
      <Dialog title={language.t("workspace.delete.title")} fit>
        <div class="flex flex-col gap-4 pl-6 pr-2.5 pb-3">
          <div class="flex flex-col gap-1">
            <span class="text-14-regular text-text-strong">
              {language.t("workspace.delete.confirm", { name: name() })}
            </span>
            <span class="text-12-regular text-text-weak">{description()}</span>
          </div>
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="large" onClick={() => dialog.close()}>
              {language.t("common.cancel")}
            </Button>
            <Button variant="primary" size="large" disabled={data.status === "loading"} onClick={handleDelete}>
              {language.t("workspace.delete.button")}
            </Button>
          </div>
        </div>
      </Dialog>
    )
  }

  function DialogResetWorkspace(props: { root: string; directory: string }) {
    const name = createMemo(() => getFilename(props.directory))
    const [state, setState] = createStore({
      status: "loading" as "loading" | "ready" | "error",
      dirty: false,
      sessions: [] as Session[],
    })

    const refresh = async () => {
      const sessions = await globalSDK.client.session
        .list({ directory: props.directory })
        .then((x) => x.data ?? [])
        .catch(() => [])
      const active = sessions.filter((session) => session.time.archived === undefined)
      setState({ sessions: active })
    }

    onMount(() => {
      globalSDK.client.file
        .status({ directory: props.directory })
        .then((x) => {
          const files = x.data ?? []
          const dirty = files.length > 0
          setState({ status: "ready", dirty })
          void refresh()
        })
        .catch(() => {
          setState({ status: "error", dirty: false })
        })
    })

    const handleReset = () => {
      dialog.close()
      void resetWorkspace(props.root, props.directory)
    }

    const archivedCount = () => state.sessions.length

    const description = () => {
      if (state.status === "loading") return language.t("workspace.status.checking")
      if (state.status === "error") return language.t("workspace.status.error")
      if (!state.dirty) return language.t("workspace.status.clean")
      return language.t("workspace.status.dirty")
    }

    const archivedLabel = () => {
      const count = archivedCount()
      if (count === 0) return language.t("workspace.reset.archived.none")
      if (count === 1) return language.t("workspace.reset.archived.one")
      return language.t("workspace.reset.archived.many", { count })
    }

    return (
      <Dialog title={language.t("workspace.reset.title")} fit>
        <div class="flex flex-col gap-4 pl-6 pr-2.5 pb-3">
          <div class="flex flex-col gap-1">
            <span class="text-14-regular text-text-strong">
              {language.t("workspace.reset.confirm", { name: name() })}
            </span>
            <span class="text-12-regular text-text-weak">
              {description()} {archivedLabel()} {language.t("workspace.reset.note")}
            </span>
          </div>
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="large" onClick={() => dialog.close()}>
              {language.t("common.cancel")}
            </Button>
            <Button variant="primary" size="large" disabled={state.status === "loading"} onClick={handleReset}>
              {language.t("workspace.reset.button")}
            </Button>
          </div>
        </div>
      </Dialog>
    )
  }

  const activeRoute = {
    session: "",
    sessionProject: "",
    directory: "",
  }

  createEffect(
    on(
      () => {
        return [pageReady(), route().slug, params.id, currentProject()?.worktree, currentDir()] as const
      },
      ([ready, slug, id, root, dir]) => {
        if (!ready || !slug || !dir) {
          activeRoute.session = ""
          activeRoute.sessionProject = ""
          activeRoute.directory = ""
          return
        }

        if (!id) {
          activeRoute.session = ""
          activeRoute.sessionProject = ""
          activeRoute.directory = ""
          return
        }

        const session = `${slug}/${id}`

        if (!root) {
          activeRoute.session = session
          activeRoute.directory = dir
          activeRoute.sessionProject = ""
          return
        }

        if (server.projects.last() !== root) server.projects.touch(root)

        const changed = session !== activeRoute.session || dir !== activeRoute.directory
        if (changed) {
          activeRoute.session = session
          activeRoute.directory = dir
          activeRoute.sessionProject = syncSessionRoute(dir, id, root)
          return
        }

        if (root === activeRoute.sessionProject) return
        activeRoute.directory = dir
        activeRoute.sessionProject = rememberSessionRoute(dir, id, root)
      },
    ),
  )

  createEffect(() => {
    const sidebarWidth = sidebarPanelOpened() && !pluginPreviewSidecarOpen() ? layout.sidebar.width() : 48
    document.documentElement.style.setProperty("--dialog-left-margin", `${sidebarWidth}px`)
  })

  // A popped-out plugin app reserves NO left chrome: no rail, no sidebar, and no
  // gutter where they used to be. Just the chat and the app.
  const side = createMemo(() =>
    pluginPopoutRoute()
      ? 0
      : sidebarRailOnly() || pluginPreviewSidecarOpen()
        ? 64
        : Math.max(layout.sidebar.width(), 330),
  )
  const panel = createMemo(() => Math.max(side() - 64, 0))
  const mainLeft = createMemo(() =>
    pluginPopoutRoute()
      ? "0px"
      : !pluginPreviewSidecarOpen() && (sidebarPanelOpened() || sidebarHovering() || (state.peeked && !sidebarRailOnly()))
        ? `${side()}px`
        : "4rem",
  )

  const loadedSessionDirs = new Set<string>()

  createEffect(
    on(
      visibleSessionDirs,
      (dirs) => {
        if (dirs.length === 0) {
          loadedSessionDirs.clear()
          return
        }

        const next = new Set(dirs)
        for (const directory of next) {
          if (loadedSessionDirs.has(directory)) continue
          void globalSync.project.loadSessions(directory)
        }

        loadedSessionDirs.clear()
        for (const directory of next) {
          loadedSessionDirs.add(directory)
        }
      },
      { defer: true },
    ),
  )

  function handleDragStart(event: unknown) {
    const id = getDraggableId(event)
    if (!id) return
    setHoverProject(undefined)
    setStore("activeProject", id)
  }

  function handleDragOver(event: DragEvent) {
    const { draggable, droppable } = event
    if (draggable && droppable) {
      const projects = layout.projects.list()
      const fromIndex = projects.findIndex((p) => p.worktree === draggable.id.toString())
      const toIndex = projects.findIndex((p) => p.worktree === droppable.id.toString())
      if (fromIndex !== toIndex && toIndex !== -1) {
        layout.projects.move(draggable.id.toString(), toIndex)
      }
    }
  }

  function handleDragEnd() {
    setStore("activeProject", undefined)
  }

  function workspaceIds(project: LocalProject | undefined) {
    if (!project) return []
    const local = project.worktree
    const dirs = [local, ...(project.sandboxes ?? [])]
    const active = currentProject()
    const directory = pathKey(active?.worktree ?? "") === pathKey(project.worktree) ? currentDir() : undefined
    const extra =
      directory && pathKey(directory) !== pathKey(local) && !dirs.some((item) => pathKey(item) === pathKey(directory))
        ? directory
        : undefined
    const pending = extra ? WorktreeState.get(server.scope(), extra)?.status === "pending" : false

    const ordered = effectiveWorkspaceOrder(local, dirs, store.workspaceOrder[project.worktree])
    if (pending && extra) return [local, extra, ...ordered.filter((item) => item !== local)]
    if (!extra) return ordered
    if (pending) return ordered
    return [...ordered, extra]
  }

  const projectForDirectory = (directory: string) => {
    const key = workspaceKey(directory)
    return (
      layout.projects
        .list()
        .find(
          (item) =>
            workspaceKey(item.worktree) === key || item.sandboxes?.some((sandbox) => workspaceKey(sandbox) === key),
        ) ?? { worktree: directory, name: getFilename(directory), expanded: false }
    )
  }
  const sidebarPanelProject = createMemo(() => {
    const project = currentProject()
    if (project) return project
    const directory = currentDir() || sidebarAppDirectory()
    return directory ? projectForDirectory(directory) : undefined
  })
  const sidebarAppsProject = createMemo(() => {
    const directory = sidebarAppDirectory()
    if (!directory) return
    const root = projectRoot(directory)
    return layout.projects.list().find((project) => pathKey(project.worktree) === pathKey(root)) ?? projectForDirectory(root)
  })
  const sidebarAppsProjectIcon = createMemo(() => {
    const project = sidebarAppsProject()
    if (!project || !("icon" in project)) return
    return project.icon
  })
  const [sidebarPluginApps] = createResource(sidebarAppDirectory, async (directory) => {
    if (!directory) return []
    const result = await globalSDK
      .createClient({
        directory,
        throwOnError: true,
      })
      .plugin.apps()
    return (result.data ?? []) as RuntimePluginApp[]
  })
  const pluginOriginForSession = (session: Session) => {
    const app = appSidebarPluginThreadOrigin({
      sessionTitle: session.title,
      apps: sidebarPluginApps() ?? [],
    })
    if (!app) return
    return {
      id: app.id,
      title: app.title,
      icon: app.icon,
      capabilities: app.capabilities,
      href: pluginAppSessionHref({ dir: base64Encode(session.directory), id: app.id }),
    }
  }

  const configForDirectory = (directory: string) => {
    const [data] = globalSync.child(directory, { bootstrap: false })
    if (Object.keys(data.config).length === 0) return globalSync.data.config
    return data.config
  }

  const viewConfigForDirectory = (directory: string) => projectManagementConfig(configForDirectory(directory))
  const globalViewConfig = createMemo(() => projectManagementConfig(globalSync.data.config))
  const viewFeatureEnabled = createMemo(() => viewEnabled(globalViewConfig(), store.threadView))
  const viewLayer = createMemo(() => layerKind(globalViewConfig(), store.threadView))

  const includePermission = (directory: string) => (item: Parameters<typeof permission.autoResponds>[0]) =>
    !permission.autoResponds(item, directory)
  const projectNotificationKind = (project: LocalProject) => {
    const dirs = workspaceIds(project)
    // Most urgent first: a pending question (permission) needs the user, then a
    // running turn, then an unopened finished response.
    if (
      dirs.some((directory) => {
        const [data] = globalSync.child(directory, { bootstrap: false })
        return hasProjectPermissions(data.permission, includePermission(directory))
      })
    ) {
      return "permission" as const
    }
    if (
      dirs.some((directory) => {
        const [data] = globalSync.child(directory, { bootstrap: false })
        return Object.values((data.session_status ?? {}) as Record<string, { type?: string } | undefined>).some(
          (status) => status?.type === "busy",
        )
      })
    ) {
      return "running" as const
    }
    if (dirs.some((directory) => hasUnseenResponseNotification(notification.project.unseen(directory)))) {
      return "response" as const
    }
  }

  const derivedThreadStage = (session: Session): ThreadStage => {
    const [data] = globalSync.child(session.directory, { bootstrap: false })
    if (threadHasFeedbackRequest(data, session.id, includePermission(session.directory))) return "blocked"
    if (threadIsActive(data, session.id, includePermission(session.directory))) return "doing"
    return "todo"
  }

  const resolveProjectViewFor = (project: { id?: string; name?: string; worktree: string }) =>
    resolveProjectView({
      project,
      config: viewConfigForDirectory(project.worktree),
      local: store.threadView,
    })

  const resolveThreadViewFor = (session: Session) => {
    const project = projectForDirectory(session.directory)
    const projectView = resolveProjectViewFor(project)
    return resolveThreadView({
      session,
      project,
      projectView,
      config: viewConfigForDirectory(session.directory),
      local: store.threadView,
      derivedStage: derivedThreadStage(session),
    })
  }

  const threadTitle = (session: Session) => resolveThreadViewFor(session).label
  const threadViewAlias = (session: Session) => resolveThreadViewFor(session).title
  const threadContext = (session: Session): SessionItemContext => {
    const project = projectForDirectory(session.directory)
    const projectLabel = resolveProjectViewFor(project).label
    const [data] = globalSync.child(session.directory, { bootstrap: false })
    const workspace =
      workspaceKey(session.directory) === workspaceKey(project.worktree)
        ? undefined
        : workspaceLabel(session.directory, data.vcs?.branch, "id" in project ? project.id : undefined)
    return {
      project: projectLabel,
      workspace,
      stage: resolveThreadViewFor(session).stageLabel,
      pinned: threadPinned(session),
    }
  }
  const canOpenSessionDirectory = (_session?: Session) =>
    Boolean(platform.platform === "desktop" && platform.openPath && server.isLocal())
  const openDirectoryPath = (directory: string) => {
    if (!canOpenSessionDirectory() || !platform.openPath) return
    return platform.openPath(directory).catch((err) => {
      console.error("[layout] open directory failed", err)
      showToast({
        title: "Folder could not be opened",
        description: layoutOpenDirectoryFailedMessage(),
      })
    })
  }
  const openSessionDirectory = (session: Session) => openDirectoryPath(session.directory)
  const copyDirectoryPath = (directory: string) =>
    navigator.clipboard.writeText(directory).then(() => {
      showToast({
        variant: "success",
        icon: "circle-check",
        title: language.t("session.share.copy.copied"),
        description: directory,
      })
    })
  const forkSession = (session: Session) => {
    prefetchSession(session, "high")
    void import("@/components/dialog-fork").then((x) => {
      dialog.show(() => <x.DialogFork sessionID={session.id} directory={session.directory} />)
    })
  }
  const threadPinned = (session: Session) => {
    if (store.threadView.pinned[threadViewKey(session)]) return true
    return isPinnedThread({
      session,
      project: projectForDirectory(session.directory),
      config: viewConfigForDirectory(session.directory),
      local: store.threadView,
    })
  }

  const setViewKind = (kind: ViewLayerKind) => {
    setStore("threadView", "kind", kind)
  }

  const touchLocalLayer = () => {
    if (viewLayer() !== "shared") return
    setViewKind("merged")
  }

  const renameProjectView = (project: { worktree: string }, name: string) => {
    touchLocalLayer()
    const key = projectViewKey(project)
    const current = store.threadView.projects[key]
    setStore("threadView", "projects", key, { ...current, name, updatedAt: Date.now() })
  }

  const hasLocalProjectViewName = (project: { worktree: string }) =>
    !!store.threadView.projects[projectViewKey(project)]?.name

  const promoteProjectViewName = (project: { id?: string; worktree: string }) => {
    const local = store.threadView.projects[projectViewKey(project)]
    if (!local?.name && !local?.code) return

    const current = globalViewConfig()
    const existing = current?.naming?.projects ?? []
    const sameTarget = (item: (typeof existing)[number]) =>
      (!!project.id && item.match.projectID === project.id) ||
      (!!item.match.worktree && workspaceKey(item.match.worktree) === workspaceKey(project.worktree)) ||
      (!!item.match.directory && workspaceKey(item.match.directory) === workspaceKey(project.worktree))

    return globalSync
      .updateConfig({
        view: {
          projectManagement: {
            ...current,
            enabled: true,
            kind: current?.kind ?? "merged",
            naming: {
              ...current?.naming,
              enabled: true,
              kind: current?.naming?.kind ?? "merged",
              projects: [
                ...existing.filter((item) => !sameTarget(item)),
                {
                  match: {
                    worktree: project.worktree,
                    ...(project.id ? { projectID: project.id } : {}),
                  },
                  ...(local.name ? { name: local.name } : {}),
                  ...(local.code ? { code: local.code } : {}),
                },
              ],
            },
          },
        },
      } satisfies Config)
      .then(() => {
        showToast({
          variant: "success",
          icon: "circle-check",
          title: language.t("sidebar.project.promoteSuccess"),
          description: language.t("sidebar.project.promoteSuccessDescription"),
        })
      })
      .catch((err) => {
        console.error("[layout] project label save failed", err)
        showToast({
          title: "Project label could not be saved",
          description: layoutProjectLabelSaveFailedMessage(),
        })
      })
  }

  const renameThreadViewTitle = (session: Session, title: string) => {
    touchLocalLayer()
    const key = threadViewKey(session)
    const current = store.threadView.threads[key]
    setStore("threadView", "threads", key, { ...current, title, updatedAt: Date.now() })
  }

  const hasLocalThreadViewTitle = (session: Session) => !!store.threadView.threads[threadViewKey(session)]?.title

  const promoteThreadViewTitle = (session: Session) => {
    const local = store.threadView.threads[threadViewKey(session)]
    if (!local?.title) return

    const current = globalViewConfig()
    const existing = current?.naming?.threads ?? []

    return globalSync
      .updateConfig({
        view: {
          projectManagement: {
            ...current,
            enabled: true,
            kind: current?.kind ?? "merged",
            naming: {
              ...current?.naming,
              enabled: true,
              kind: current?.naming?.kind ?? "merged",
              threads: [
                ...existing.filter((item) => item.match.sessionID !== session.id),
                {
                  match: {
                    directory: session.directory,
                    sessionID: session.id,
                  },
                  title: local.title,
                  ...(local.stage ? { stage: local.stage } : {}),
                  ...(local.priority ? { priority: local.priority } : {}),
                  ...(local.tags?.length ? { tags: local.tags } : {}),
                },
              ],
            },
          },
        },
      } satisfies Config)
      .then(() => {
        showToast({
          variant: "success",
          icon: "circle-check",
          title: language.t("sidebar.thread.promoteSuccess"),
          description: language.t("sidebar.thread.promoteSuccessDescription"),
        })
      })
      .catch((err) => {
        console.error("[layout] thread label save failed", err)
        showToast({
          title: "Thread label could not be saved",
          description: layoutThreadLabelSaveFailedMessage(),
        })
      })
  }

  const resetThreadViewTitle = (session: Session) => {
    const key = threadViewKey(session)
    setStore(
      "threadView",
      "threads",
      produce((draft) => {
        delete draft[key]
      }),
    )
  }

  const togglePinned = (session: Session) => {
    touchLocalLayer()
    const key = threadViewKey(session)
    if (!store.threadView.pinned[key]) {
      setStore("threadView", "enabled", true)
      setStore("threadView", "pinnedOpen", true)
    }
    setStore(
      "threadView",
      "pinned",
      produce((draft) => {
        if (draft[key]) {
          delete draft[key]
          return
        }
        draft[key] = {
          projectKey: projectViewKey(projectForDirectory(session.directory)),
          directory: session.directory,
          sessionID: session.id,
          pinnedAt: Date.now(),
        }
      }),
    )
  }
  const threadActions = {
    isPinned: threadPinned,
    togglePinned,
    forkSession,
  }

  const sidebarProject = createMemo(() => {
    if (sidebarPanelOpened()) return currentProject()
    const hovered = hoverProjectData()
    if (hovered) return hovered
    return currentProject()
  })

  function handleWorkspaceDragStart(event: unknown) {
    const id = getDraggableId(event)
    if (!id) return
    setStore("activeWorkspace", id)
  }

  function handleWorkspaceDragOver(event: DragEvent) {
    const { draggable, droppable } = event
    if (!draggable || !droppable) return

    const project = sidebarProject()
    if (!project) return

    const ids = workspaceIds(project)
    const fromIndex = ids.findIndex((dir) => dir === draggable.id.toString())
    const toIndex = ids.findIndex((dir) => dir === droppable.id.toString())
    if (fromIndex === -1 || toIndex === -1) return
    if (fromIndex === toIndex) return

    const result = ids.slice()
    const [item] = result.splice(fromIndex, 1)
    if (!item) return
    result.splice(toIndex, 0, item)
    setStore(
      "workspaceOrder",
      project.worktree,
      result.filter((directory) => pathKey(directory) !== pathKey(project.worktree)),
    )
  }

  function handleWorkspaceDragEnd() {
    setStore("activeWorkspace", undefined)
  }

  const createWorkspace = async (project: LocalProject) => {
    clearSidebarHoverState()
    const created = await globalSDK.client.worktree
      .create({ directory: project.worktree })
      .then((x) => x.data)
      .catch((err) => {
        console.error("[layout] workspace create failed", err)
        showToast({
          title: language.t("workspace.create.failed.title"),
          description: layoutWorkspaceCreateFailedMessage(),
        })
        return undefined
      })

    if (!created?.directory) return

    setWorkspaceName(created.directory, created.branch, project.id, created.branch)

    const local = project.worktree
    const key = pathKey(created.directory)
    const root = pathKey(local)

    setBusy(created.directory, true)
    WorktreeState.pending(server.scope(), created.directory)
    setStore("workspaceExpanded", key, true)
    if (key !== created.directory) {
      setStore("workspaceExpanded", created.directory, true)
    }
    setStore("workspaceOrder", project.worktree, (prev) => {
      const existing = prev ?? []
      const next = existing.filter((item) => {
        const id = pathKey(item)
        return id !== root && id !== key
      })
      return [created.directory, ...next]
    })

    globalSync.child(created.directory)
    navigateWithSidebarReset(`/${base64Encode(created.directory)}/session`)
    return created
  }

  const workspaceSidebarCtx: WorkspaceSidebarContext = {
    currentDir,
    navList: currentSessions,
    sidebarExpanded,
    sidebarHovering,
    clearHoverProjectSoon,
    prefetchSession,
    archiveSession,
    viewTitle: threadTitle,
    viewAlias: threadViewAlias,
    projectLabel: () => undefined,
    pluginOrigin: pluginOriginForSession,
    context: threadContext,
    isPinned: threadPinned,
    togglePinned,
    hasLocalViewTitle: hasLocalThreadViewTitle,
    promoteViewTitle: promoteThreadViewTitle,
    renameViewTitle: renameThreadViewTitle,
    resetViewTitle: resetThreadViewTitle,
    renameSessionTitle,
    openDirectory: openSessionDirectory,
    canOpenDirectory: canOpenSessionDirectory,
    forkSession,
    workspaceName,
    renameWorkspace,
    editorOpen,
    openEditor,
    closeEditor,
    setEditor,
    InlineEditor,
    isBusy,
    workspaceExpanded: (directory, local) =>
      store.workspaceExpanded[pathKey(directory)] ?? store.workspaceExpanded[directory] ?? local,
    setWorkspaceExpanded: (directory, value) => {
      const key = pathKey(directory)
      setStore("workspaceExpanded", key, value)
      if (key === directory) return
      setStore("workspaceExpanded", directory, value)
    },
    showResetWorkspaceDialog: (root, directory) =>
      dialog.show(() => <DialogResetWorkspace root={root} directory={directory} />),
    showDeleteWorkspaceDialog: (root, directory) =>
      dialog.show(() => <DialogDeleteWorkspace root={root} directory={directory} />),
    setScrollContainerRef: (el, mobile) => {
      if (!mobile) scrollContainerRef = el
    },
  }

  const projectSidebarCtx: ProjectSidebarContext = {
    currentDir,
    currentProject,
    sidebarOpened: sidebarPanelOpened,
    sidebarHovering,
    selectedProjectNavigates: () => Boolean(activePluginID()),
    hoverProject: () => state.hoverProject,
    onProjectMouseEnter: (worktree, event) => aim.enter(worktree, event),
    onProjectMouseLeave: (worktree) => aim.leave(worktree),
    onProjectFocus: (worktree) => aim.activate(worktree),
    onHoverOpenChanged: (worktree, hoverOpen) => {
      if (!hoverOpen && state.hoverProject && state.hoverProject !== worktree) return
      setState("hoverProject", hoverOpen ? worktree : undefined)
    },
    navigateToProject,
    openSidebar: () => layout.sidebar.open(),
    closeProject,
    showEditProjectDialog,
    toggleProjectWorkspaces,
    reviewSmartworkAccess,
    workspacesEnabled: (project) => project.vcs === "git" && layout.sidebar.workspaces(project.worktree)(),
    smartworkCloudEnabled: (project) => smartworkProjectServicesEnabledFor(smartworkProjectServices(), project.worktree),
    workspaceIds,
    workspaceLabel,
    projectLabel: (project) => resolveProjectViewFor(project).label,
    projectNotificationKind,
    openProjectDirectory: openDirectoryPath,
    canOpenProjectDirectory: () => canOpenSessionDirectory(),
    sessionProps: {
      navList: currentSessions,
      sidebarExpanded,
      clearHoverProjectSoon,
      prefetchSession,
      archiveSession,
      viewTitle: threadTitle,
      viewAlias: threadViewAlias,
      projectLabel: () => undefined,
      pluginOrigin: pluginOriginForSession,
      context: threadContext,
      isPinned: threadPinned,
      togglePinned,
      hasLocalViewTitle: hasLocalThreadViewTitle,
      promoteViewTitle: promoteThreadViewTitle,
      renameViewTitle: renameThreadViewTitle,
      resetViewTitle: resetThreadViewTitle,
      renameSessionTitle,
      openDirectory: openSessionDirectory,
      canOpenDirectory: canOpenSessionDirectory,
      forkSession,
      now: sortNow,
    },
  }

  const SidebarNavRow = (props: {
    icon: IconName
    label: string
    action: string
    onClick: () => void
    active?: boolean
    disabled?: boolean
    keybind?: string
  }) => (
    <button
      type="button"
      data-action={props.action}
      disabled={props.disabled}
      onClick={props.onClick}
      class="group/navrow flex h-8 w-full items-center gap-3 rounded-md px-2.5 text-left text-14-regular transition-colors disabled:cursor-default disabled:opacity-45"
      classList={{
        "bg-surface-base-active text-text-strong": !!props.active,
        "text-text-base hover:bg-surface-raised-base-hover hover:text-text-strong": !props.active,
      }}
    >
      <span data-sidebar-nav-icon={props.icon} class="text-icon-base group-hover/navrow:text-icon-strong">
        <Icon name={props.icon} size="small" />
      </span>
      <span class="min-w-0 flex-1 truncate">{props.label}</span>
      <Show when={props.keybind}>
        {(keybind) => (
          <span class="shrink-0 rounded-full bg-surface-raised-base px-1.5 py-0.5 text-11-medium text-text-weak tabular-nums opacity-0 transition-opacity group-hover/navrow:opacity-100">
            {keybind()}
          </span>
        )}
      </Show>
    </button>
  )

  const SidebarPrimaryActions = () => {
    const newSessionHref = createMemo(() => {
      if (params.dir) return `/${params.dir}/session`
      const project = currentProject()
      if (!project) return ""
      return `/${base64Encode(project.worktree)}/session`
    })

    return (
      <div data-component="sidebar-primary-actions" class="shrink-0 pt-1 pb-0">
        <div class="flex flex-col gap-0.5">
          <SidebarNavRow
            icon="new-session"
            label="New chat"
            action="sidebar-new-chat"
            active={creatingSession()}
            disabled={!newSessionHref()}
            keybind={command.keybind("session.new")}
            onClick={() => {
              const href = newSessionHref()
              if (!href) return
              navigateWithSidebarReset(href)
            }}
          />
          <SidebarNavRow
            icon="magnifying-glass"
            label="Search"
            action="sidebar-search"
            keybind={command.keybind("command.palette")}
            onClick={() => command.show()}
          />
        </div>
      </div>
    )
  }

  const ProjectManagementThreads = (props: { mobile?: boolean; threads: Accessor<Session[]> }) => {
    const togglePinnedThreads = () => setStore("threadView", "pinnedOpen", !store.threadView.pinnedOpen)
    const togglePinnedThreadsFromKeyboard = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      togglePinnedThreads()
    }
    const rowProps = {
      sidebarExpanded,
      clearHoverProjectSoon,
      prefetchSession,
      archiveSession,
      viewTitle: threadTitle,
      viewAlias: threadViewAlias,
      projectLabel: () => undefined,
      pluginOrigin: pluginOriginForSession,
      context: threadContext,
      isPinned: threadPinned,
      togglePinned,
      hasLocalViewTitle: hasLocalThreadViewTitle,
      promoteViewTitle: promoteThreadViewTitle,
      renameViewTitle: renameThreadViewTitle,
      resetViewTitle: resetThreadViewTitle,
      renameSessionTitle,
      openDirectory: openSessionDirectory,
      canOpenDirectory: canOpenSessionDirectory,
      forkSession,
      now: sortNow,
    }

    return (
      <div
        data-component="sidebar-pinned-section"
        data-layer={viewLayer()}
        class="shrink-0"
        classList={{ "mt-2 mb-3": props.threads().length > 0 }}
      >
        <Show when={props.threads().length > 0}>
          <div>
            <div
              role="button"
              tabIndex={0}
              class="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-13-medium text-text-weak hover:bg-surface-raised-base-hover hover:text-text-base focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
              data-action="thread-pinned-toggle"
              aria-expanded={store.threadView.pinnedOpen}
              onClick={togglePinnedThreads}
              onKeyDown={togglePinnedThreadsFromKeyboard}
            >
              <span class="truncate">{language.t("sidebar.threads.pinned")}</span>
              <Icon
                name={store.threadView.pinnedOpen ? "chevron-down" : "chevron-right"}
                size="small"
                class="shrink-0 text-icon-base"
              />
            </div>
            <Show when={store.threadView.pinnedOpen}>
              <div
                data-component="thread-pinned-list"
                data-sidebar-scrollbar=""
                class="max-h-52 overflow-y-auto pr-1 flex flex-col gap-1"
              >
                <For each={props.threads().slice(0, 6)}>
                  {(session) => (
                    <SessionItem
                      {...rowProps}
                      session={session}
                      list={props.threads()}
                      navList={props.threads}
                      slug={base64Encode(session.directory)}
                      dense
                      showTooltip
                      mobile={props.mobile}
                    />
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    )
  }

  const AppSidebarProviders = (providerProps: ParentProps<{ directory: string }>) => (
    <SDKProvider directory={() => providerProps.directory}>
      <SyncProvider>
        <LocalProvider>{providerProps.children}</LocalProvider>
      </SyncProvider>
    </SDKProvider>
  )

  const SidebarPanel = (panelProps: {
    project: Accessor<LocalProject | undefined>
    mobile?: boolean
    merged?: boolean
  }) => {
    const project = panelProps.project
    const merged = createMemo(() => panelProps.mobile || (panelProps.merged ?? sidebarPanelOpened()))
    const hover = createMemo(() => !panelProps.mobile && panelProps.merged === false && !sidebarPanelOpened())
    const pluginPreviewCollapsed = createMemo(() => pluginPreviewSidecarOpen() && !panelProps.mobile)
    const empty = createMemo(() => !params.dir && layout.projects.list().length === 0)
    const projectName = createMemo(() => {
      const item = project()
      if (!item) return ""
      return resolveProjectViewFor(item).label
    })
    const projectId = createMemo(() => project()?.id ?? "")
    const worktree = createMemo(() => project()?.worktree ?? "")
    const projectChatsKey = createMemo(() => {
      const dir = worktree()
      if (!dir) return ""
      return pathKey(dir)
    })
    const slug = createMemo(() => {
      const dir = worktree()
      if (!dir) return ""
      return base64Encode(dir)
    })
    const projectChatsOpen = createMemo(() => store.projectChatsExpanded[projectChatsKey()] ?? true)
    const toggleProjectChats = () => {
      if (!projectChatsKey()) return
      if (editorOpen(`project:${projectId()}`)) return
      setStore("projectChatsExpanded", projectChatsKey(), !projectChatsOpen())
    }
    const toggleProjectChatsFromKeyboard = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      toggleProjectChats()
    }
    const workspaces = createMemo(() => {
      const item = project()
      if (!item) return [] as string[]
      return workspaceIds(item)
    })
    const projectPinnedThreads = createMemo(() => {
      const item = project()
      if (!item) return [] as Session[]
      return workspaceIds(item)
        .flatMap((directory) => sortedRootSessions(globalSync.child(directory, { bootstrap: false })[0], sortNow()))
        .filter((session) => threadPinned(session))
        .sort(
          (a, b) =>
            pinnedSortValue({
              session: a,
              project: projectForDirectory(a.directory),
              config: viewConfigForDirectory(a.directory),
              local: store.threadView,
            }) -
            pinnedSortValue({
              session: b,
              project: projectForDirectory(b.directory),
              config: viewConfigForDirectory(b.directory),
              local: store.threadView,
            }),
        )
    })
    const unseenCount = createMemo(() =>
      workspaces().reduce((total, directory) => total + notification.project.unseenCount(directory), 0),
    )
    const appChatVisible = createMemo(() => !!(activePluginID() && currentDir()))
    const clearNotifications = () =>
      workspaces()
        .filter((directory) => notification.project.unseenCount(directory) > 0)
        .forEach((directory) => notification.project.markViewed(directory))
    const workspacesEnabled = createMemo(() => {
      const item = project()
      if (!item) return false
      if (item.vcs !== "git") return false
      return layout.sidebar.workspaces(item.worktree)()
    })
    return (
      <div
        data-plugin-preview-collapsed={pluginPreviewCollapsed() ? "true" : undefined}
        aria-hidden={pluginPreviewCollapsed() ? "true" : undefined}
        inert={pluginPreviewCollapsed()}
        classList={{
          "flex flex-col min-h-0 min-w-0 box-border px-3": true,
          "bg-background-base": (merged() || hover()) && !pluginPreviewCollapsed(),
          "bg-background-stronger": !merged() && !hover() && !pluginPreviewCollapsed(),
          "border-r border-border-weaker-base": appChatVisible() && !panelProps.mobile && !pluginPreviewCollapsed(),
          "flex-1 min-w-0": panelProps.mobile,
          "max-w-full overflow-hidden": panelProps.mobile,
          "opacity-100 translate-x-0": !pluginPreviewCollapsed(),
          "opacity-0 -translate-x-2 pointer-events-none w-0 px-0 overflow-hidden border-0": pluginPreviewCollapsed(),
          "transition-[width,padding,opacity,transform,background-color,border-color] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none":
            !panelProps.mobile,
        }}
        style={{
          width: pluginPreviewCollapsed() ? "0px" : panelProps.mobile ? undefined : `${panel()}px`,
        }}
      >
        <Show when={!pluginPreviewSidecarOpen()}>
          <Show when={appChatVisible()}>
            <AppSidebarProviders directory={currentDir()}>
              <AppSidebarPanel
                directory={currentDir()}
                slug={params.dir ?? base64Encode(currentDir())}
                pluginID={activePluginID()}
                page={emptyAppChatPage}
                sessionID={() => layout.appChat.sessionID(appChatKey())}
                parentSessionID={appChatParentSessionID}
                onSessionID={(sessionID) => layout.appChat.setSessionID(appChatKey(), sessionID)}
                onNewSession={() => layout.appChat.clear(appChatKey())}
                onRenameSession={renameSessionTitle}
                onArchiveSession={archiveSession}
              />
            </AppSidebarProviders>
          </Show>
          <Show when={!appChatVisible()}>
            <Show when={project()}>
              <div data-component="sidebar-folder-name" class="shrink-0 pt-3 pb-1 px-2">
                <div class="truncate text-16-medium font-bold text-text-base">{projectName()}</div>
              </div>
            </Show>
            <SidebarPrimaryActions />
            <ProjectManagementThreads mobile={panelProps.mobile} threads={projectPinnedThreads} />
            <Show
              when={project()}
              keyed
              fallback={
                <Show when={empty()}>
                  <div class="flex-1 min-h-0 -mt-4 flex items-center justify-center px-6 pb-64 text-center">
                    <div class="mt-8 flex max-w-60 flex-col items-center gap-6 text-center">
                      <div class="flex flex-col gap-3">
                        <div class="text-14-medium text-text-strong">{language.t("sidebar.empty.title")}</div>
                        <div
                          class="text-14-regular text-text-base"
                          style={{ "line-height": "var(--line-height-normal)" }}
                        >
                          {language.t("sidebar.empty.description")}
                        </div>
                      </div>
                      <Button size="large" icon="folder-add-left" onClick={chooseProject}>
                        {language.t("command.project.open")}
                      </Button>
                    </div>
                  </div>
                </Show>
              }
            >
              {(item) => (
                <div
                  data-component="sidebar-projects-section"
                  class="min-h-0 flex-1 flex flex-col overflow-hidden pt-0"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    data-component="project-context-card"
                    data-action="project-chats-toggle"
                    data-project={slug()}
                    aria-expanded={projectChatsOpen()}
                    class="group/project relative shrink-0 pb-0"
                    onClick={toggleProjectChats}
                    onKeyDown={toggleProjectChatsFromKeyboard}
                  >
                    <div class="flex h-8 w-full cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 pr-12 hover:bg-surface-raised-base-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base">
                      <div class="flex items-center gap-3 min-w-0">
                        <span data-sidebar-nav-icon="speech-bubble" class="text-icon-base group-hover/project:text-icon-strong">
                          <Icon name="speech-bubble" size="small" />
                        </span>
                        <span class="text-14-medium font-bold text-text-strong truncate">Chats</span>
                      </div>

                      <Icon
                        name={projectChatsOpen() ? "chevron-down" : "chevron-right"}
                        size="small"
                        class="shrink-0 text-icon-base"
                      />
                    </div>

                    <div
                      class="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <DropdownMenu modal={!sidebarHovering()}>
                        <DropdownMenu.Trigger
                          as={IconButton}
                          icon="dot-grid"
                          variant="ghost"
                          data-action="project-menu"
                          data-project={slug()}
                          class="shrink-0 size-6 rounded-md transition-opacity data-[expanded]:bg-surface-base-active"
                          classList={{
                            "opacity-100": panelProps.mobile || merged(),
                            "opacity-0 group-hover/project:opacity-100 group-focus-within/project:opacity-100 data-[expanded]:opacity-100":
                              !panelProps.mobile && !merged(),
                          }}
                          aria-label={language.t("common.moreOptions")}
                        />
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content class="mt-1">
                            <DropdownMenu.Item
                              onSelect={() => {
                                showEditProjectDialog(item)
                              }}
                            >
                              <DropdownMenu.ItemLabel>{language.t("common.edit")}</DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              data-action="project-workspaces-toggle"
                              data-project={slug()}
                              onSelect={() => {
                                toggleProjectWorkspaces(item)
                              }}
                            >
                              <DropdownMenu.ItemLabel>
                                {workspacesEnabled()
                                  ? language.t("sidebar.workspaces.disable")
                                  : language.t("sidebar.workspaces.enable")}
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              data-action="project-smartwork-review"
                              data-project={slug()}
                              class="group/folder-action"
                              onSelect={() => reviewSmartworkAccess(item)}
                            >
                              <DropdownMenu.ItemLabel>
                                <span class="flex min-w-0 items-center gap-2">
                                  <Icon
                                    name="cloud-upload"
                                    size="small"
                                    class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
                                  />
                                  <span class="truncate">{language.t("sidebar.smartwork.reviewAccess")}</span>
                                </span>
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              data-action="project-clear-notifications"
                              data-project={slug()}
                              disabled={unseenCount() === 0}
                              onSelect={clearNotifications}
                            >
                              <DropdownMenu.ItemLabel>
                                {language.t("sidebar.project.clearNotifications")}
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              data-action="project-archived-chats"
                              data-project={slug()}
                              onSelect={() => showArchivedChatsDialog(item)}
                            >
                              <DropdownMenu.ItemLabel>
                                {language.t("sidebar.project.archivedChats")}
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <Show when={canOpenSessionDirectory()}>
                              <DropdownMenu.Item
                                data-action="project-open-directory"
                                data-project={slug()}
                                class="group/folder-action"
                                onSelect={() => openDirectoryPath(item.worktree)}
                              >
                                <DropdownMenu.ItemLabel>
                                  <span class="flex min-w-0 items-center gap-2">
                                    <Icon
                                      name="folder"
                                      size="small"
                                      class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
                                    />
                                    <span class="truncate">{language.t("sidebar.project.openDirectory")}</span>
                                  </span>
                                </DropdownMenu.ItemLabel>
                              </DropdownMenu.Item>
                            </Show>
                            <DropdownMenu.Item
                              data-action="project-copy-directory"
                              data-project={slug()}
                              class="group/folder-action"
                              onSelect={() => void copyDirectoryPath(item.worktree)}
                            >
                              <DropdownMenu.ItemLabel>
                                <span class="flex min-w-0 items-center gap-2">
                                  <Icon
                                    name="copy"
                                    size="small"
                                    class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
                                  />
                                  <span class="truncate">{language.t("sidebar.project.copyDirectory")}</span>
                                </span>
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              data-action="project-promote-view-label"
                              data-project={slug()}
                              disabled={!hasLocalProjectViewName(item)}
                              onSelect={() => {
                                void promoteProjectViewName(item)
                              }}
                            >
                              <DropdownMenu.ItemLabel>
                                {language.t("sidebar.project.promoteViewLabel")}
                              </DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Item
                              data-action="project-close-menu"
                              data-project={slug()}
                              onSelect={() => {
                                const dir = worktree()
                                if (!dir) return
                                closeProject(dir)
                              }}
                            >
                              <DropdownMenu.ItemLabel>{language.t("common.close")}</DropdownMenu.ItemLabel>
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu>
                    </div>
                  </div>

                  <Show when={projectChatsOpen()}>
                    <div data-component="sidebar-project-list" class="min-h-0 flex-1 flex flex-col pl-6 overflow-hidden">
                      <div data-component="project-chat-list" class="min-h-0 flex-1 flex flex-col">
                        <Show
                          when={workspacesEnabled()}
                          fallback={
                            <div class="min-h-0 flex-1">
                              <LocalWorkspace
                                ctx={workspaceSidebarCtx}
                                project={item}
                                sortNow={sortNow}
                                mobile={panelProps.mobile}
                              />
                            </div>
                          }
                        >
                          <>
                            <div class="shrink-0 py-2">
                              <Button
                                size="normal"
                                icon="plus-small"
                                class="w-full"
                                onClick={() => {
                                  void createWorkspace(item)
                                }}
                              >
                                {language.t("workspace.new")}
                              </Button>
                            </div>
                            <div class="relative flex-1 min-h-0">
                              <DragDropProvider
                                onDragStart={handleWorkspaceDragStart}
                                onDragEnd={handleWorkspaceDragEnd}
                                onDragOver={handleWorkspaceDragOver}
                                collisionDetector={closestCenter}
                              >
                                <DragDropSensors />
                                <ConstrainDragXAxis />
                                <div
                                  ref={(el) => {
                                    if (!panelProps.mobile) scrollContainerRef = el
                                  }}
                                  data-sidebar-scrollbar=""
                                  class="size-full flex flex-col gap-4 overflow-y-auto py-2 pb-5 [overflow-anchor:none]"
                                >
                                  <SortableProvider ids={workspaces()}>
                                    <For each={workspaces()}>
                                      {(directory) => (
                                        <SortableWorkspace
                                          ctx={workspaceSidebarCtx}
                                          directory={directory}
                                          project={item}
                                          sortNow={sortNow}
                                          mobile={panelProps.mobile}
                                        />
                                      )}
                                    </For>
                                  </SortableProvider>
                                </div>
                                <DragOverlay>
                                  <WorkspaceDragOverlay
                                    sidebarProject={sidebarProject}
                                    activeWorkspace={() => store.activeWorkspace}
                                    workspaceLabel={workspaceLabel}
                                  />
                                </DragOverlay>
                              </DragDropProvider>
                            </div>
                          </>
                        </Show>
                      </div>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div
              class="shrink-0 px-3 py-3"
              classList={{
                hidden: store.gettingStartedDismissed || !(providers.all().length > 0 && providers.paid().length === 0),
              }}
            >
              <div class="rounded-xl bg-background-base shadow-xs-border-base" data-component="getting-started">
                <div class="p-3 flex flex-col gap-6">
                  <div class="flex flex-col gap-2">
                    <div class="text-14-medium text-text-strong">{language.t("sidebar.gettingStarted.title")}</div>
                    <div class="text-14-regular text-text-base" style={{ "line-height": "var(--line-height-normal)" }}>
                      {language.t("sidebar.gettingStarted.line1")}
                    </div>
                    <div class="text-14-regular text-text-base" style={{ "line-height": "var(--line-height-normal)" }}>
                      {language.t("sidebar.gettingStarted.line2")}
                    </div>
                  </div>
                  <div data-component="getting-started-actions">
                    <Button size="large" icon="plus-small" onClick={connectProvider}>
                      {language.t("command.provider.connect")}
                    </Button>
                    <Button size="large" variant="ghost" onClick={() => setStore("gettingStartedDismissed", true)}>
                      {language.t("toast.update.action.notYet")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Show>
        </Show>
      </div>
    )
  }

  const projects = () => layout.projects.list()
  const sidebarProjects = createMemo(() => {
    const projects = layout.projects.list()
    if (projects.length > 0) return projects
    const directory = sidebarAppDirectory()
    return directory ? [projectForDirectory(projectRoot(directory))] : []
  })
  const projectOverlay = () => <ProjectDragOverlay projects={sidebarProjects} activeProject={() => store.activeProject} />
  const sidebarContent = (mobile?: boolean) => (
    <SidebarContent
      mobile={mobile}
      opened={() => (mobile ? layout.sidebar.opened() : sidebarPanelOpened())}
      aimMove={aim.move}
      projects={sidebarProjects}
      renderProject={(project) => (
        <SortableProject ctx={projectSidebarCtx} project={project} sortNow={sortNow} mobile={mobile} />
      )}
      renderProjectApps={(project) => {
        const appsProject = sidebarAppsProject()
        if (!appsProject?.worktree) return
        if (pathKey(appsProject.worktree) !== pathKey(project.worktree)) return
        return (
          <div data-sidebar-project-apps="" class="w-full flex justify-center">
            <ProjectPluginRail
              directory={sidebarAppDirectory}
              apps={() => sidebarPluginApps() ?? []}
              mobile={mobile}
              projectColor={() => sidebarAppsProjectIcon()?.color}
              projectPalette={() => sidebarAppsProjectIcon()?.palette}
            />
          </div>
        )
      }}
      handleDragStart={handleDragStart}
      handleDragEnd={handleDragEnd}
      handleDragOver={handleDragOver}
      openProjectLabel={language.t("command.project.open")}
      openProjectKeybind={() => command.keybind("project.open")}
      onOpenProject={chooseProject}
      renderProjectOverlay={projectOverlay}
      settingsLabel={() => language.t("sidebar.settings")}
      settingsKeybind={() => command.keybind("settings.open")}
      onOpenSettings={openSettings}
      smartworkLabel={() => "Smartwork"}
      onOpenSmartwork={() => navigateWithSidebarReset(smartworkViewPath("session", smartworkNavigationProjectSlug()))}
      renderPanel={() =>
        mobile ? <SidebarPanel project={sidebarPanelProject} mobile /> : <SidebarPanel project={sidebarPanelProject} merged />
      }
    />
  )

  return (
    <Show
      when={miniChatRoute()}
      fallback={
        <div class="relative bg-background-base flex-1 min-h-0 min-w-0 flex flex-col select-none [&_input]:select-text [&_textarea]:select-text [&_[contenteditable]]:select-text">
          {autoselecting() ?? ""}
          <Titlebar />
          <div class="flex-1 min-h-0 min-w-0 flex">
            <div class="flex-1 min-h-0 relative">
              <div
                data-sidebar-panel-state={sidebarPanelOpened() ? "open" : "collapsed"}
                class="size-full relative overflow-x-hidden"
              >
                <nav
                  aria-label={language.t("sidebar.nav.projectsAndSessions")}
                  data-component="sidebar-nav-desktop"
                  classList={{
                    // A popout window has one chat and one app. There is nothing to
                    // navigate to, so there is no rail and no sidebar.
                    "!hidden": pluginPopoutRoute(),
                    "hidden lg:block": true,
                    "absolute inset-y-0 left-0": true,
                    "z-10": true,
                    "transition-[width] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none":
                      !state.sizing,
                  }}
                  style={{ width: `${side()}px` }}
                  ref={(el) => {
                    setState("nav", el)
                  }}
                  onMouseEnter={() => {
                    disarm()
                  }}
                  onMouseLeave={() => {
                    aim.reset()
                    if (!sidebarHovering()) return

                    arm()
                  }}
                >
                  <div class="@container w-full h-full contain-strict">{sidebarContent()}</div>
                </nav>

                <Show when={sidebarPanelOpened() && !pluginPreviewSidecarOpen()}>
                  <div
                    class="hidden lg:block absolute inset-y-0 z-30 w-0 overflow-visible"
                    style={{ left: `${side()}px` }}
                    onPointerDown={() => setState("sizing", true)}
                  >
                    <ResizeHandle
                      direction="horizontal"
                      size={layout.sidebar.width()}
                      min={330}
                      max={typeof window === "undefined" ? 1000 : window.innerWidth * 0.3 + 64}
                      onResize={(w) => {
                        setState("sizing", true)
                        if (sizet !== undefined) clearTimeout(sizet)
                        sizet = window.setTimeout(() => setState("sizing", false), 120)
                        layout.sidebar.resize(w)
                      }}
                    />
                  </div>
                </Show>

                <div class="lg:hidden">
                  <div
                    classList={{
                      "fixed inset-x-0 top-10 bottom-0 z-40 transition-opacity duration-200 motion-reduce:transition-none": true,
                      "opacity-100 pointer-events-auto": layout.mobileSidebar.opened(),
                      "opacity-0 pointer-events-none": !layout.mobileSidebar.opened(),
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) layout.mobileSidebar.hide()
                    }}
                  />
                  <nav
                    aria-label={language.t("sidebar.nav.projectsAndSessions")}
                    data-component="sidebar-nav-mobile"
                    classList={{
                      "@container fixed top-10 bottom-0 left-0 z-50 w-full max-w-[400px] overflow-hidden border-r border-border-weaker-base bg-background-base transition-transform duration-200 ease-out motion-reduce:transition-none": true,
                      "translate-x-0": layout.mobileSidebar.opened(),
                      "-translate-x-full": !layout.mobileSidebar.opened(),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {sidebarContent(true)}
                  </nav>
                </div>

                <div
                  data-plugin-main-shell
                  classList={{
                    "absolute inset-0": true,
                    "lg:inset-y-0 lg:right-0 lg:left-[var(--main-left)]": true,
                    "z-20": true,
                    "transition-[left] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[left] motion-reduce:transition-none":
                      !state.sizing,
                  }}
                  style={{
                    "--main-left": mainLeft(),
                  }}
                >
                  <main
                    classList={{
                      "size-full overflow-x-hidden flex flex-col items-start contain-strict bg-background-base": true,
                    }}
                  >
                    <Show when={!autoselecting.loading} fallback={<div class="size-full" />}>
                      <ThreadActionsProvider actions={threadActions}>{props.children}</ThreadActionsProvider>
                    </Show>
                  </main>
                </div>

                <div
                  classList={{
                    "hidden lg:flex absolute inset-y-0 left-16 z-30": true,
                    "opacity-100 translate-x-0 pointer-events-auto":
                      state.peeked && !sidebarPanelOpened() && !sidebarRailOnly(),
                    "opacity-0 -translate-x-2 pointer-events-none":
                      !state.peeked || sidebarPanelOpened() || sidebarRailOnly(),
                    "transition-[opacity,transform] motion-reduce:transition-none": true,
                    "duration-180 ease-out": state.peeked && !sidebarPanelOpened() && !sidebarRailOnly(),
                    "duration-120 ease-in": !state.peeked || sidebarPanelOpened() || sidebarRailOnly(),
                  }}
                  onMouseMove={disarm}
                  onMouseEnter={() => {
                    disarm()
                    aim.reset()
                  }}
                  onPointerDown={disarm}
                  onMouseLeave={() => {
                    arm()
                  }}
                >
                  <Show when={peekProject()}>
                    <SidebarPanel project={peekProject} merged={false} />
                  </Show>
                </div>

                <div
                  classList={{
                    "hidden lg:block pointer-events-none absolute inset-y-0 right-0 z-25 overflow-hidden": true,
                    "opacity-100 translate-x-0": state.peeked && !sidebarPanelOpened() && !sidebarRailOnly(),
                    "opacity-0 -translate-x-2": !state.peeked || sidebarPanelOpened() || sidebarRailOnly(),
                    "transition-[opacity,transform] motion-reduce:transition-none": true,
                    "duration-180 ease-out": state.peeked && !sidebarPanelOpened() && !sidebarRailOnly(),
                    "duration-120 ease-in": !state.peeked || sidebarPanelOpened() || sidebarRailOnly(),
                  }}
                  style={{ left: `calc(4rem + ${panel()}px)` }}
                >
                  <div class="h-full w-px" style={{ "box-shadow": "var(--shadow-sidebar-overlay)" }} />
                </div>
              </div>
            </div>
            {/*
              Opt-IN, not opt-out.

              The debug bar holds a requestAnimationFrame loop open for the entire
              life of the window and re-renders itself four times a second. That
              keeps the compositor repainting every frame even when the app is
              idle, which costs ~15fps and — on Windows — makes the mouse cursor
              stutter and flicker across the whole window, not just over the bar.

              It is a measuring instrument, and it was changing what it measured.
              Set VITE_OA_DEBUG_BAR=1 to bring it back.
            */}
            {import.meta.env.DEV && import.meta.env["VITE_OA_DEBUG_BAR"] === "1" && <DebugBar />}
          </div>
          <Toast.Region />
        </div>
      }
    >
      <div class="relative h-dvh min-h-0 w-screen overflow-hidden bg-background-base select-none [&_input]:select-text [&_textarea]:select-text [&_[contenteditable]]:select-text">
        <ThreadActionsProvider actions={threadActions}>{props.children}</ThreadActionsProvider>
        <Toast.Region />
      </div>
    </Show>
  )
}
